/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Forms API
 * API for managing questions and forms within an organization. All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Request to add an item to a form
 */
export interface AddFormItemRequest {
    'itemType': FormItemType;
    /**
     * Position of the item in the form
     */
    'position': number;
    /**
     * Question UUID (required when itemType is QUESTION)
     */
    'questionId'?: string;
    'content'?: FormItemContent;
}


/**
 * An AI-generated suggestion for a form question answer
 */
export interface AiSuggestion {
    /**
     * The question UUID this suggestion is for
     */
    'questionId': string;
    /**
     * The AI-suggested answer value (format depends on question type)
     */
    'suggestedValue': any;
}
/**
 * Response containing AI-generated suggestions for form answers
 */
export interface AiSuggestionsResponse {
    /**
     * List of AI-generated suggestions (one per question with a suggestion)
     */
    'suggestions': Array<AiSuggestion>;
}
/**
 * An answer to a question in a form submission
 */
export interface AnswerResponse {
    /**
     * Answer UUID
     */
    'id': string;
    /**
     * Form submission UUID
     */
    'submissionId': string;
    /**
     * Question UUID
     */
    'questionId': string;
    /**
     * Question version UUID (version at time of answer)
     */
    'questionVersionId': string;
    /**
     * Question text (denormalized for display)
     */
    'questionText'?: string;
    'questionType'?: QuestionType;
    'value': AnswerResponseValue;
    /**
     * The options of the question version this answer was given on, for the choice types. A SINGLE_CHOICE / MULTI_CHOICE `value` holds option keys, so this is what a consumer resolves them to labels against — the same lookup the letter placeholder, the PDF and the AI recap already do server-side. Null for every other question type. 
     */
    'options'?: Array<QuestionOption> | null;
    /**
     * How many fields the TEXT_LIST question version this answer was given on showed, which is the most entries an edit of it may hold. Null for every other question type. 
     */
    'fieldCount'?: number | null;
    /**
     * Whether this answer has been edited after initial submission
     */
    'hasBeenEdited'?: boolean;
    /**
     * Number of times this answer has been edited
     */
    'editCount'?: number;
    /**
     * Last edit timestamp (epoch milliseconds)
     */
    'lastEditedAt'?: number | null;
    /**
     * Name of the user who last edited the answer
     */
    'lastEditedBy'?: string | null;
    /**
     * Whether a newer version of the question exists than the one this answer was given on
     */
    'hasNewerVersion'?: boolean;
    /**
     * The current (latest) question text, only included when hasNewerVersion is true
     */
    'currentQuestionText'?: string | null;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
}


/**
 * @type AnswerResponseValue
 * The answer value. Type depends on question type: - TEXT_SHORT/TEXT_LONG: string - YES_NO: boolean - SINGLE_CHOICE: an option key, or `{key, text}` when the option picked   is marked `allowsFreeText` — the key it names plus the text typed into it - MULTI_CHOICE: array of option keys, at most one of which may be the   `{key, text}` object above - AGREEMENT: boolean - DATE: string (ISO 8601 date) - NUMBER: number - TEXT_LIST: array of non-blank strings, in order, at most the question\'s   `fieldCount` of them 
 */
export type AnswerResponseValue = Array<AnswerResponseValueOneOfInner> | TaggedChoiceValue | boolean | number | string;

/**
 * @type AnswerResponseValueOneOfInner
 */
export type AnswerResponseValueOneOfInner = TaggedChoiceValue | string;

/**
 * An immutable version of an answer
 */
export interface AnswerVersionResponse {
    /**
     * Version UUID
     */
    'id': string;
    /**
     * Answer UUID this version belongs to
     */
    'answerId': string;
    /**
     * Sequential version number (1-based)
     */
    'versionNumber': number;
    'value': AnswerVersionResponseValue;
    'statusAtSave': FormSubmissionStatus;
    /**
     * User UUID who saved this version (set for behandelaar saves)
     */
    'createdByUserId'?: string | null;
    /**
     * Name of the user who saved this version (denormalized)
     */
    'createdByUserName'?: string | null;
    /**
     * Client UUID who submitted this version (set for portal submissions)
     */
    'createdByClientId'?: string | null;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
    /**
     * Optional reason provided when this version was saved (e.g. for an after-the-fact correction)
     */
    'modificationReason'?: string | null;
}


/**
 * @type AnswerVersionResponseValue
 * The answer value captured in this version
 */
export type AnswerVersionResponseValue = Array<AnswerResponseValueOneOfInner> | TaggedChoiceValue | boolean | number | string;

/**
 * All versions for an answer ordered by version number descending
 */
export interface AnswerVersionsResponse {
    /**
     * Answer UUID
     */
    'answerId': string;
    /**
     * Versions ordered by version number descending (newest first)
     */
    'versions': Array<AnswerVersionResponse>;
}
/**
 * Response containing form completion status for all client-form combinations in an appointment. Provides a \"completion matrix\" view showing which clients have completed which forms, useful for tracking progress in group appointments. 
 */
export interface AppointmentFormStatusResponse {
    /**
     * Appointment UUID
     */
    'appointmentId': string;
    /**
     * All client-form status combinations. Each entry represents one client-form pair with its completion status. For a group appointment with 3 clients and 2 forms, this will contain 6 entries. 
     */
    'statuses': Array<ClientFormStatusItem>;
    /**
     * Progress summary per form. Shows how many clients have completed each form. Useful for displaying \"3 of 5 clients completed\" summaries. 
     */
    'formProgress': Array<FormProgressItem>;
    /**
     * Total number of forms linked to this appointment type
     */
    'totalForms': number;
    /**
     * Total number of clients in the appointment
     */
    'totalClients': number;
}
/**
 * Response containing form submissions for an appointment, grouped by client. For single-client appointments, contains one client entry. For group appointments, contains multiple client entries. 
 */
export interface AppointmentFormSubmissionsResponse {
    /**
     * Appointment UUID
     */
    'appointmentId': string;
    /**
     * Whether this is a group appointment with multiple clients
     */
    'isGroupAppointment': boolean;
    /**
     * Form submissions grouped by client
     */
    'clients': Array<ClientFormSubmissions>;
    /**
     * Total number of form submissions across all clients
     */
    'totalSubmissions': number;
}
/**
 * List of appointment type form links
 */
export interface AppointmentTypeFormLinkListResponse {
    'links': Array<AppointmentTypeFormLinkResponse>;
    /**
     * Total number of form links
     */
    'totalCount': number;
}
/**
 * A link between an appointment type and a form
 */
export interface AppointmentTypeFormLinkResponse {
    /**
     * Form link UUID
     */
    'id': string;
    /**
     * Appointment type UUID
     */
    'appointmentTypeId': string;
    /**
     * Form UUID
     */
    'formId': string;
    'form'?: FormSummary;
    /**
     * Order in which forms are displayed (1-based)
     */
    'displayOrder': number;
    /**
     * Whether completion of this form is required after appointments
     */
    'isRequired': boolean;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
    /**
     * Last update timestamp (epoch milliseconds)
     */
    'updatedAt'?: number | null;
}
/**
 * A single answer submission from a behandelaar
 */
export interface BehandelaarAnswerSubmission {
    /**
     * Question UUID to answer
     */
    'questionId': string;
    /**
     * The answer value. Type depends on question type: - TEXT_SHORT/TEXT_LONG: string - YES_NO: boolean - SINGLE_CHOICE: an option key, or `{key, text}` when the option picked   is marked `allowsFreeText` — the key it names plus the text typed into it - MULTI_CHOICE: array of option keys, at most one of which may be the   `{key, text}` object above - AGREEMENT: boolean - DATE: string (ISO 8601 date) - NUMBER: number - TEXT_LIST: array of non-blank strings, in order, at most the question\'s   `fieldCount` of them 
     */
    'value': any;
}
/**
 * Form completion status for a specific client-form combination
 */
export interface ClientFormStatusItem {
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * Client full name
     */
    'clientName': string;
    /**
     * Form UUID
     */
    'formId': string;
    /**
     * Form title
     */
    'formName': string;
    'status': FormCompletionStatus;
    /**
     * UUID of the FormSubmission for this (form, appointment, client) triple. Submissions are eagerly created in DRAFT status when the appointment is created (or lazily backfilled when the status is queried), so this is always populated and can be used directly with the bulk-save answers endpoint. 
     */
    'submissionId': string;
    /**
     * Submission timestamp if submitted (epoch milliseconds)
     */
    'submittedAt'?: number | null;
}


/**
 * Paginated list of form submissions for a client
 */
export interface ClientFormSubmissionListResponse {
    'content': Array<ClientFormSubmissionResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of submissions
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * A form submission record for a client, including form details
 */
export interface ClientFormSubmissionResponse {
    /**
     * Present only when requested with includeAnswers=true: the submission\'s answers, newest question order preserved.
     */
    'answers'?: Array<AnswerResponse>;
    /**
     * Submission UUID
     */
    'id': string;
    /**
     * Form UUID that was submitted
     */
    'formId': string;
    /**
     * Title of the form that was submitted
     */
    'formTitle': string;
    'submissionType': SubmissionType;
    /**
     * User UUID who submitted (for BEHANDELAAR submissions). A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns.
     */
    'submittedByUserId'?: string | null;
    /**
     * Client UUID who submitted (for CLIENT submissions)
     */
    'submittedByClientId'?: string | null;
    /**
     * Submission timestamp (epoch milliseconds)
     */
    'submittedAt': number;
    /**
     * Associated appointment UUID (if submitted during an appointment). An afspraak id. No appointment operation is part of the public API.
     */
    'appointmentId'?: string | null;
    /**
     * Name of the appointment type (if submitted during an appointment)
     */
    'appointmentTitle'?: string | null;
    /**
     * Appointment start time in epoch milliseconds (if submitted during an appointment)
     */
    'appointmentStartTime'?: number | null;
    /**
     * Display name of the submitter. For BEHANDELAAR submissions this is the user\'s full name, for CLIENT submissions this is \"Cliënt via portaal\"
     */
    'submitterName'?: string | null;
    /**
     * Set when the submission is of a form Scrivio provisions rather than one the organisation authored. A `DOSSIERNOTITIE` submission is an afspraak\'s dossiernotitie: filed on the afspraak for no client, one shared text for every participant, its one answer the note.
     */
    'systemForm'?: SystemForm | null;
}


/**
 * Form submissions for a specific client in an appointment
 */
export interface ClientFormSubmissions {
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * Client full name
     */
    'clientName': string;
    /**
     * Form submissions for this client, ordered by submission time descending
     */
    'submissions': Array<FormSubmissionResponse>;
    /**
     * Number of form submissions for this client
     */
    'submissionCount': number;
}
/**
 * Pending forms status for a single client in a group appointment
 */
export interface ClientPendingForms {
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * Client full name
     */
    'clientName': string;
    /**
     * List of pending forms for this client
     */
    'pendingForms': Array<PendingForm>;
    /**
     * Number of forms completed for this client
     */
    'completedCount': number;
    /**
     * Number of required forms for this client
     */
    'requiredCount': number;
    /**
     * Total number of forms linked for this client
     */
    'totalCount': number;
    /**
     * Whether all required forms have been completed for this client
     */
    'allRequiredCompleted': boolean;
}
/**
 * Request to create a new form
 */
export interface CreateFormRequest {
    /**
     * Form title
     */
    'title': string;
    /**
     * Client-facing form introduction, printed above the questions
     */
    'description'?: string | null;
    /**
     * Author-facing general instruction for the AI form fill, applied to every question of this form. Never printed and never shown to the client. 
     */
    'aiInstruction'?: string | null;
    /**
     * Whether this form is used during appointments
     */
    'forAppointment': boolean;
    /**
     * Whether this form is available to clients via portal
     */
    'forClient': boolean;
    /**
     * Whether submissions of this form may be read by the AI recap on the client dossier. Omitted means included, which is what every form did before the flag existed. Deliberately carries no `default:` — see UpdateFormRequest. 
     */
    'includeInAiRecap'?: boolean;
}
/**
 * Request to create a new standalone form submission
 */
export interface CreateFormSubmissionRequest {
    /**
     * UUID of the form template to create a submission for
     */
    'formId': string;
    /**
     * Optional UUID of the client this submission belongs to. Set for an internal, behandelaar-filled dossier form: the submission is attached to the client\'s dossier with no appointment, and is never exposed to the client portal (the portal reads form assignments, not submissions). The client must belong to the same organization. 
     */
    'clientId'?: string;
}
/**
 * Request to create a new question
 */
export interface CreateQuestionRequest {
    /**
     * Human-readable key for the question (e.g., \'client_name\'). Must be unique within the organization.
     */
    'key': string;
    'type': QuestionType;
    /**
     * The question text
     */
    'text': string;
    /**
     * Client-facing explanation shown under the question and printed
     */
    'description'?: string | null;
    /**
     * Author-facing instruction telling the AI what to extract from the transcript for this question. Never printed and never shown to the client. 
     */
    'aiInstruction'?: string | null;
    /**
     * Options for choice-type questions (SINGLE_CHOICE, MULTI_CHOICE). Required when type is SINGLE_CHOICE or MULTI_CHOICE. 
     */
    'options'?: Array<QuestionOption> | null;
    /**
     * How many single-line fields a TEXT_LIST question shows (1-10). Ignored for every other type. Absent keeps the latest version\'s count on an update, and is 3 when the question is or becomes a TEXT_LIST without one. No `default:` is declared because a default on a request-body property survives deserialisation and resets the field. 
     */
    'fieldCount'?: number | null;
    /**
     * Whether an answer is required
     */
    'required': boolean;
    /**
     * Whether to add this question to the reusable question library. If true (default), the question will be available in the library picker for adding to other forms. If false, the question is an inline form-specific question. 
     */
    'inLibrary'?: boolean;
}


/**
 * Response containing all answers for a form
 */
export interface FormAnswersResponse {
    /**
     * Client UUID
     */
    'clientId': string;
    /**
     * Form UUID
     */
    'formId': string;
    'submission'?: FormSubmissionResponse;
    /**
     * List of answers
     */
    'answers': Array<AnswerResponse>;
    /**
     * Total number of answers
     */
    'totalCount': number;
}
/**
 * How far a form submission has got. Saving answers does not change a submission\'s `FormSubmissionStatus`, so DRAFT alone cannot tell an empty draft from a filled-in report: - PENDING: No answers saved yet (an empty draft) - IN_PROGRESS: Answers saved, but not yet marked as reviewed (beoordeeld) - SUBMITTED: Marked as reviewed (beoordeeld) 
 */

export const FormCompletionStatus = {
    Pending: 'PENDING',
    InProgress: 'IN_PROGRESS',
    Submitted: 'SUBMITTED'
} as const;

export type FormCompletionStatus = typeof FormCompletionStatus[keyof typeof FormCompletionStatus];


/**
 * Content for STATIC_TEXT or DOCUMENT_LINK form items
 */
export interface FormItemContent {
    /**
     * Text content for STATIC_TEXT items
     */
    'text'?: string;
    /**
     * Title for DOCUMENT_LINK items
     */
    'title'?: string;
    /**
     * URL for DOCUMENT_LINK items
     */
    'url'?: string;
}
/**
 * A form item (question, static text, or document link)
 */
export interface FormItemResponse {
    /**
     * Form item UUID
     */
    'id': string;
    /**
     * Position of the item in the form (1-based ordering)
     */
    'position': number;
    'itemType': FormItemType;
    'question'?: QuestionResponse;
    'content'?: FormItemContent;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
}


/**
 * Types of items that can be added to a form: - QUESTION: A reference to a Question entity - STATIC_TEXT: Static text content (instructions, descriptions) - DOCUMENT_LINK: A link to an external document 
 */

export const FormItemType = {
    Question: 'QUESTION',
    StaticText: 'STATIC_TEXT',
    DocumentLink: 'DOCUMENT_LINK'
} as const;

export type FormItemType = typeof FormItemType[keyof typeof FormItemType];


/**
 * List of forms
 */
export interface FormListResponse {
    'forms': Array<FormResponse>;
    /**
     * Total number of forms
     */
    'totalCount': number;
}
/**
 * Progress tracking for a single form across all clients
 */
export interface FormProgressItem {
    /**
     * Form UUID
     */
    'formId': string;
    /**
     * Form title
     */
    'formName': string;
    /**
     * Number of clients who have completed this form
     */
    'completedCount': number;
    /**
     * Total number of clients in the appointment
     */
    'totalClients': number;
    /**
     * Whether this form is required for the appointment type
     */
    'isRequired': boolean;
    /**
     * Whether all clients have completed this form
     */
    'allCompleted': boolean;
}
/**
 * A form with all its items
 */
export interface FormResponse {
    /**
     * Form UUID
     */
    'id': string;
    /**
     * Organization this form belongs to
     */
    'organizationId': string;
    /**
     * Form title
     */
    'title': string;
    /**
     * Client-facing form introduction, printed above the questions
     */
    'description'?: string | null;
    /**
     * Author-facing general instruction for the AI form fill, applied to every question of this form. Never printed and never shown to the client. 
     */
    'aiInstruction'?: string | null;
    'status': FormStatus;
    /**
     * Whether this form is used during appointments
     */
    'forAppointment': boolean;
    /**
     * Whether this form is available to clients via portal
     */
    'forClient': boolean;
    /**
     * Whether submissions of this form may be read by the AI recap on the client dossier. False keeps the form out of it entirely: its answers reach neither the prompt nor the recap\'s data sources, and cannot make a recap generate on their own. 
     */
    'includeInAiRecap': boolean;
    /**
     * Form items ordered by position
     */
    'items': Array<FormItemResponse>;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
    /**
     * Last update timestamp (epoch milliseconds)
     */
    'updatedAt': number;
}


/**
 * Status of a form: - DRAFT: Form is being created/edited, not yet available for use - ACTIVE: Form is available for submissions - DEPRECATED: Form is no longer available for new submissions 
 */

export const FormStatus = {
    Draft: 'DRAFT',
    Active: 'ACTIVE',
    Deprecated: 'DEPRECATED'
} as const;

export type FormStatus = typeof FormStatus[keyof typeof FormStatus];


/**
 * Full detail of a form submission including the form template structure, answers, and linked entity information. 
 */
export interface FormSubmissionDetailResponse {
    /**
     * Submission UUID
     */
    'id': string;
    'status': FormSubmissionStatus;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
    'createdBy'?: FormSubmissionDetailResponseCreatedBy | null;
    'submissionType': SubmissionType;
    'form': FormResponse;
    /**
     * List of answers for this submission
     */
    'answers': Array<AnswerResponse>;
    'linkedClient'?: FormSubmissionDetailResponseLinkedClient | null;
    'linkedAppointment'?: FormSubmissionDetailResponseLinkedAppointment | null;
}


/**
 * Information about who created this submission
 */
export interface FormSubmissionDetailResponseCreatedBy {
    /**
     * User UUID who submitted (for BEHANDELAAR submissions)
     */
    'userId'?: string | null;
    /**
     * Full name of the user who submitted
     */
    'userName'?: string | null;
    /**
     * Client UUID who submitted (for CLIENT submissions)
     */
    'clientId'?: string | null;
}
/**
 * Linked appointment information (if submission is tied to an appointment). The afspraak the submission belongs to. No appointment operation is part of the public API.
 */
export interface FormSubmissionDetailResponseLinkedAppointment {
    /**
     * Appointment UUID
     */
    'id'?: string;
    /**
     * Appointment start time (epoch milliseconds)
     */
    'startTime'?: number;
    /**
     * Name of the appointment type
     */
    'appointmentTypeName'?: string | null;
}
/**
 * Linked client information (if submission is for a specific client)
 */
export interface FormSubmissionDetailResponseLinkedClient {
    /**
     * Client UUID
     */
    'id'?: string;
    /**
     * Full name of the client
     */
    'name'?: string;
}
/**
 * A single audit timeline entry for a form submission. Includes both events on the submission itself and on its child answers. 
 */
export interface FormSubmissionHistoryEntry {
    /**
     * Audit log entry UUID
     */
    'id': string;
    /**
     * When the action occurred (ISO 8601)
     */
    'occurredAt': string;
    /**
     * User who performed the action
     */
    'userId'?: string | null;
    /**
     * Display name of the user who performed the action
     */
    'userName'?: string | null;
    /**
     * Admin user ID when the action was performed during an impersonation session. The UI can use this to show \"X (impersonated by Y)\". 
     */
    'impersonatedByUserId'?: string | null;
    /**
     * Display name (or email) of the admin user when the action was performed during an impersonation session. Resolved server-side against the `admin_user` table so the UI can render \"X (geïmpersonifieerd door Y)\" without a second lookup. 
     */
    'impersonatedByName'?: string | null;
    /**
     * Audit action. For form submission history this is typically one of: CREATE, UPDATE, DELETE, FINALIZE, STATUS_CHANGE. 
     */
    'action': string;
    /**
     * Resource the entry refers to (FormSubmission or Answer)
     */
    'resourceType': string;
    /**
     * ID of the resource the entry refers to
     */
    'resourceId': string;
    /**
     * For mutation actions, the changes made. Field names map to objects with `before`/`after` values (sensitive fields may be redacted). 
     */
    'changes'?: { [key: string]: any; } | null;
    /**
     * Base question ID for Answer entries. Resolved server-side via the answer\'s question version so the frontend doesn\'t need a second lookup. 
     */
    'questionId'?: string | null;
    /**
     * Question text from the answer\'s question version for Answer entries, so the frontend can render labels like \"Subjectief: \'old\' → \'new\'\". 
     */
    'questionText'?: string | null;
}
/**
 * Paginated audit history for a form submission
 */
export interface FormSubmissionHistoryResponse {
    'content': Array<FormSubmissionHistoryEntry>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Page size
     */
    'size': number;
    /**
     * Total number of audit entries belonging to this submission
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * List of form submissions
 */
export interface FormSubmissionListResponse {
    'submissions': Array<FormSubmissionResponse>;
    /**
     * Total number of submissions
     */
    'totalCount': number;
}
/**
 * A form submission record
 */
export interface FormSubmissionResponse {
    /**
     * Submission UUID
     */
    'id': string;
    /**
     * Form UUID that was submitted
     */
    'formId': string;
    'submissionType': SubmissionType;
    'status': FormSubmissionStatus;
    /**
     * Client UUID the form was submitted about. For group appointments, this identifies which client within the group this submission pertains to. For single-client appointments, this matches the appointment\'s clientId. 
     */
    'clientId'?: string | null;
    /**
     * Full name of the client the form was submitted about
     */
    'clientName'?: string | null;
    /**
     * User UUID who submitted (for BEHANDELAAR submissions). A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns.
     */
    'submittedByUserId'?: string | null;
    /**
     * Client UUID who submitted (for CLIENT submissions)
     */
    'submittedByClientId'?: string | null;
    /**
     * Submission timestamp (epoch milliseconds)
     */
    'submittedAt': number;
    /**
     * Associated appointment UUID (if submitted during an appointment). An afspraak id. No appointment operation is part of the public API.
     */
    'appointmentId'?: string | null;
    /**
     * Name of the appointment type (if submitted during an appointment)
     */
    'appointmentTitle'?: string | null;
    /**
     * Appointment start time in epoch milliseconds (if submitted during an appointment)
     */
    'appointmentStartTime'?: number | null;
    /**
     * Display name of the submitter. For BEHANDELAAR submissions this is the user\'s full name, for CLIENT submissions this is \"Cliënt via portaal\"
     */
    'submitterName'?: string | null;
}


/**
 * Status of a form submission: - DRAFT: Form submission created but not yet reviewed - REVIEWED: Form submission has been reviewed by a behandelaar 
 */

export const FormSubmissionStatus = {
    Draft: 'DRAFT',
    Reviewed: 'REVIEWED'
} as const;

export type FormSubmissionStatus = typeof FormSubmissionStatus[keyof typeof FormSubmissionStatus];


/**
 * Summary information about a form
 */
export interface FormSummary {
    /**
     * Form UUID
     */
    'id': string;
    /**
     * Form title
     */
    'title': string;
    /**
     * Form description
     */
    'description'?: string | null;
    'status': FormStatus;
    /**
     * Number of items in the form
     */
    'itemCount'?: number;
}


/**
 * Whether the organization uses formulieren
 */
export interface FormsUsageResponse {
    /**
     * True when the organization has at least one non-deleted form
     */
    'usesForms': boolean;
}
/**
 * Request to link a form to an appointment type
 */
export interface LinkFormToAppointmentTypeRequest {
    /**
     * Form UUID to link
     */
    'formId': string;
    /**
     * Display order (auto-assigned if not provided)
     */
    'displayOrder'?: number | null;
    /**
     * Whether the form is required (defaults to false)
     */
    'isRequired'?: boolean | null;
}
/**
 * Paginated list of form submissions for an organization
 */
export interface OrganizationFormSubmissionListResponse {
    'content': Array<OrganizationFormSubmissionResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of submissions
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
/**
 * A form submission record for organization-level listing
 */
export interface OrganizationFormSubmissionResponse {
    /**
     * Submission UUID
     */
    'id': string;
    /**
     * Form UUID that was submitted
     */
    'formId': string;
    /**
     * Title of the form template
     */
    'formTitle': string;
    /**
     * User UUID who created the submission (for BEHANDELAAR submissions)
     */
    'createdByUserId'?: string | null;
    /**
     * Full name of the user who created the submission
     */
    'createdByUserName'?: string | null;
    /**
     * Submission timestamp (epoch milliseconds)
     */
    'submittedAt': number;
    /**
     * Client UUID the form was submitted about (if linked to a client)
     */
    'clientId'?: string | null;
    /**
     * Full name of the linked client (if any)
     */
    'clientName'?: string | null;
    /**
     * Associated appointment UUID (if submitted during an appointment)
     */
    'appointmentId'?: string | null;
    /**
     * Name of the appointment type (if submitted during an appointment)
     */
    'appointmentTitle'?: string | null;
    /**
     * Appointment start time in epoch milliseconds (if submitted during an appointment)
     */
    'appointmentStartTime'?: number | null;
    /**
     * Whether the linked appointment was cancelled. Null if no appointment is linked.
     */
    'appointmentCancelled'?: boolean | null;
    'completionStatus': FormCompletionStatus;
}


/**
 * A form pending completion after an appointment
 */
export interface PendingForm {
    'form': FormSummary;
    /**
     * Whether this form must be completed
     */
    'isRequired': boolean;
    /**
     * Whether this form has already been completed for this appointment
     */
    'isCompleted': boolean;
    /**
     * Submission ID if the form has been completed
     */
    'submissionId'?: string | null;
    /**
     * Submission timestamp if completed (epoch milliseconds)
     */
    'submittedAt'?: number | null;
}
/**
 * Response containing forms pending completion after an appointment.  For single-client appointments, use the `forms` field directly. For group appointments (multiple clients), use the `clients` field which provides per-client pending form status. 
 */
export interface PendingFormsResponse {
    /**
     * Appointment UUID
     */
    'appointmentId': string;
    /**
     * Whether this is a group appointment with multiple clients. When true, use the `clients` field for per-client form status. When false, use the `forms` field for the single client. 
     */
    'isGroupAppointment': boolean;
    /**
     * List of forms linked to this appointment type. For single-client appointments, this shows the completion status for that client. For group appointments, this is still populated for backwards compatibility but `clients` should be used for per-client status. 
     */
    'forms': Array<PendingForm>;
    /**
     * Per-client pending forms breakdown. Only populated when isGroupAppointment is true. Each entry contains the pending forms status for one client in the group. 
     */
    'clients'?: Array<ClientPendingForms> | null;
    /**
     * Total number of form submissions completed across all clients. For group appointments, this is the sum across all clients. 
     */
    'completedCount': number;
    /**
     * Total number of required form submissions. For group appointments, this is requiredFormsPerClient * numberOfClients. 
     */
    'requiredCount': number;
    /**
     * Total number of form submissions expected. For group appointments, this is linkedForms * numberOfClients. 
     */
    'totalCount': number;
    /**
     * Whether all required forms have been completed for all clients
     */
    'allRequiredCompleted'?: boolean;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Response indicating whether a question has answers
 */
export interface QuestionHasAnswersResponse {
    /**
     * Question UUID
     */
    'questionId': string;
    /**
     * True if the question has any answers
     */
    'hasAnswers': boolean;
}
/**
 * List of questions
 */
export interface QuestionListResponse {
    'questions': Array<QuestionResponse>;
    /**
     * Total number of questions
     */
    'totalCount': number;
}
/**
 * An option for choice-type questions
 */
export interface QuestionOption {
    /**
     * Unique identifier for this option (used in answers)
     */
    'key': string;
    /**
     * Display text for this option
     */
    'label': string;
    /**
     * Whether picking this option also accepts typed text — an \"Anders, namelijk\". At most one option per question may set it; a second is refused with a 400.  An answer naming such an option stores `{key, text}` instead of the bare key (see the `value` descriptions on the answer schemas). Absent means false; no `default:` is declared because a default on a request-body property survives deserialisation and resets the field. 
     */
    'allowsFreeText'?: boolean;
}
/**
 * A question with its latest version
 */
export interface QuestionResponse {
    /**
     * Question UUID
     */
    'id': string;
    /**
     * Organization this question belongs to
     */
    'organizationId': string;
    /**
     * Human-readable key for the question (e.g., \'client_name\')
     */
    'key': string;
    'currentVersion': QuestionVersionResponse;
    /**
     * Question creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
    /**
     * When the question was archived (epoch milliseconds), null if not archived
     */
    'archivedAt'?: number | null;
    /**
     * Whether this question has any answers. Questions with answers cannot be deleted but can be archived.
     */
    'hasAnswers': boolean;
    /**
     * Whether this question is in the reusable question library. Library questions can be added to multiple forms. Inline questions (inLibrary=false) are form-specific and not shown in the library picker. 
     */
    'inLibrary': boolean;
}
/**
 * Types of questions available in forms: - TEXT_SHORT: Short text input (single line) - TEXT_LONG: Long text input (multi-line) - YES_NO: Binary yes/no choice - SINGLE_CHOICE: Select one from multiple options - MULTI_CHOICE: Select multiple from options - AGREEMENT: Agreement/consent checkbox - DATE: Date input - NUMBER: Numeric input - TEXT_LIST: Several short text inputs (\"Meerdere korte antwoorden\"); how many is   the version\'s `fieldCount` 
 */

export const QuestionType = {
    TextShort: 'TEXT_SHORT',
    TextLong: 'TEXT_LONG',
    YesNo: 'YES_NO',
    SingleChoice: 'SINGLE_CHOICE',
    MultiChoice: 'MULTI_CHOICE',
    Agreement: 'AGREEMENT',
    Date: 'DATE',
    Number: 'NUMBER',
    TextList: 'TEXT_LIST'
} as const;

export type QuestionType = typeof QuestionType[keyof typeof QuestionType];


/**
 * List of question versions
 */
export interface QuestionVersionListResponse {
    /**
     * Question UUID
     */
    'questionId': string;
    /**
     * All versions ordered by version number descending
     */
    'versions': Array<QuestionVersionResponse>;
    /**
     * Total number of versions
     */
    'totalCount': number;
}
/**
 * A specific version of a question
 */
export interface QuestionVersionResponse {
    /**
     * Version UUID
     */
    'id': string;
    /**
     * Version number (1-based)
     */
    'version': number;
    'type': QuestionType;
    /**
     * The question text
     */
    'text': string;
    /**
     * Client-facing explanation shown under the question and printed
     */
    'description'?: string | null;
    /**
     * Author-facing instruction telling the AI what to extract from the transcript for this question. Never printed and never shown to the client. 
     */
    'aiInstruction'?: string | null;
    /**
     * Options for choice-type questions
     */
    'options'?: Array<QuestionOption> | null;
    /**
     * How many single-line fields a TEXT_LIST question shows, and so the most entries its answer may hold. Null for every other type. 
     */
    'fieldCount'?: number | null;
    /**
     * Whether an answer is required
     */
    'required': boolean;
    /**
     * Creation timestamp (epoch milliseconds)
     */
    'createdAt': number;
}


/**
 * Request to reorder form items
 */
export interface ReorderItemsRequest {
    /**
     * Ordered list of form item UUIDs. The position of each item will be updated to match its index in this array (1-based). 
     */
    'itemIds': Array<string>;
}
/**
 * Request to save multiple answers for a form submission
 */
export interface SaveAnswersRequest {
    /**
     * List of answers to save
     */
    'answers': Array<BehandelaarAnswerSubmission>;
}
/**
 * Type of form submission: - BEHANDELAAR: Submitted by a user (care provider) - CLIENT: Submitted by a client via portal 
 */

export const SubmissionType = {
    Behandelaar: 'BEHANDELAAR',
    Client: 'CLIENT'
} as const;

export type SubmissionType = typeof SubmissionType[keyof typeof SubmissionType];


/**
 * A form Scrivio provisions for every organisation; it cannot be authored.
 */

export const SystemForm = {
    Dossiernotitie: 'DOSSIERNOTITIE'
} as const;

export type SystemForm = typeof SystemForm[keyof typeof SystemForm];


/**
 * A choice answer that also carries typed text — what a SINGLE_CHOICE value is, or one entry of a MULTI_CHOICE array, when the option picked is marked `allowsFreeText`.  An ordinary choice stays the bare option key it has always been; only the free-text option is tagged, so which option was chosen is never inferred from the text. `text` must not be blank: \"picked it and typed nothing\" is the bare key. 
 */
export interface TaggedChoiceValue {
    /**
     * The key of the option chosen; must be one the question offers
     */
    'key': string;
    /**
     * The text typed into that option
     */
    'text': string;
}
/**
 * Request to update an answer value
 */
export interface UpdateAnswerRequest {
    'value': UpdateAnswerRequestValue;
    /**
     * Reason for the edit (stored in audit trail)
     */
    'reason'?: string | null;
}
/**
 * @type UpdateAnswerRequestValue
 * The new answer value. Type must match the question type: - TEXT_SHORT/TEXT_LONG: string - YES_NO: boolean - SINGLE_CHOICE: an option key, or `{key, text}` when the option picked   is marked `allowsFreeText` — the key it names plus the text typed into it - MULTI_CHOICE: array of option keys, at most one of which may be the   `{key, text}` object above - AGREEMENT: boolean - DATE: string (ISO 8601 date) - NUMBER: number - TEXT_LIST: array of non-blank strings, in order, at most the question\'s   `fieldCount` of them 
 */
export type UpdateAnswerRequestValue = Array<AnswerResponseValueOneOfInner> | TaggedChoiceValue | boolean | number | string;

/**
 * Request to update form link settings
 */
export interface UpdateAppointmentTypeFormLinkRequest {
    /**
     * New display order
     */
    'displayOrder'?: number | null;
    /**
     * Whether the form is required
     */
    'isRequired'?: boolean | null;
}
/**
 * Request to update an existing form
 */
export interface UpdateFormRequest {
    /**
     * Form title
     */
    'title': string;
    /**
     * Client-facing form introduction, printed above the questions
     */
    'description'?: string | null;
    /**
     * Author-facing general instruction for the AI form fill, applied to every question of this form. Never printed and never shown to the client. 
     */
    'aiInstruction'?: string | null;
    /**
     * Whether this form is used during appointments
     */
    'forAppointment': boolean;
    /**
     * Whether this form is available to clients via portal
     */
    'forClient': boolean;
    /**
     * Whether submissions of this form may be read by the AI recap on the client dossier. Omitting the property leaves the stored value alone; it deliberately carries no `default:`, which would make an absent property indistinguishable from an explicit \"include\" and silently reset the flag on every partial update. 
     */
    'includeInAiRecap'?: boolean;
}
/**
 * Request to update a form submission. All fields are optional. Omitting a field means no change, while explicitly setting null clears the value. 
 */
export interface UpdateFormSubmissionRequest {
    /**
     * UUID of the form template to change to. Only allowed if no answers have been saved yet. The new form must be ACTIVE and belong to the same organization. 
     */
    'formId'?: string;
    /**
     * UUID of the client to link this submission to, or null to unlink. Client must belong to the same organization. 
     */
    'clientId'?: string | null;
    /**
     * UUID of the appointment to link this submission to, or null to unlink. Appointment must belong to the same organization.  When linking (null → value): TranscriptionSessions owned by this submission are transferred to the appointment.  When unlinking (value → null): TranscriptionSessions stay on the appointment. 
     */
    'appointmentId'?: string | null;
}
/**
 * Request to update a question (creates a new version). The question type can be changed. When switching to a non-choice type, options will be ignored. When switching to a choice type, options must be provided. 
 */
export interface UpdateQuestionRequest {
    'type'?: QuestionType;
    /**
     * The question text
     */
    'text': string;
    /**
     * Client-facing explanation shown under the question and printed
     */
    'description'?: string | null;
    /**
     * Author-facing instruction telling the AI what to extract from the transcript for this question. Never printed and never shown to the client. 
     */
    'aiInstruction'?: string | null;
    /**
     * Options for choice-type questions (SINGLE_CHOICE, MULTI_CHOICE). Must be provided when the question type is SINGLE_CHOICE or MULTI_CHOICE. 
     */
    'options'?: Array<QuestionOption> | null;
    /**
     * How many single-line fields a TEXT_LIST question shows (1-10). Ignored for every other type. Absent keeps the latest version\'s count on an update, and is 3 when the question is or becomes a TEXT_LIST without one. No `default:` is declared because a default on a request-body property survives deserialisation and resets the field. 
     */
    'fieldCount'?: number | null;
    /**
     * Whether an answer is required
     */
    'required': boolean;
}



/**
 * AnswersApi - axios parameter creator
 */
export const AnswersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns a single answer with its current value.
         * @summary Get an answer by ID
         * @param {string} answerId Answer UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAnswer: async (answerId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'answerId' is not null or undefined
            assertParamExists('getAnswer', 'answerId', answerId)
            const localVarPath = `/api/v1/answers/{answerId}`
                .replace(`{${"answerId"}}`, encodeURIComponent(String(answerId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all immutable versions of an answer ordered by version number descending. Each version captures the answer value, the form submission status at the time of save, and the actor who saved it. The current version (highest number) reflects the active value on the answer. 
         * @summary Get version history for an answer
         * @param {string} answerId Answer UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAnswerVersions: async (answerId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'answerId' is not null or undefined
            assertParamExists('getAnswerVersions', 'answerId', answerId)
            const localVarPath = `/api/v1/answers/{answerId}/versions`
                .replace(`{${"answerId"}}`, encodeURIComponent(String(answerId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all answers submitted for a specific form by a client. Includes the most recent submission if available. 
         * @summary Get all answers for a form
         * @param {string} clientId Client UUID
         * @param {string} formId Form UUID
         * @param {string} [submissionId] Filter by specific submission ID (optional)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormAnswers: async (clientId: string, formId: string, submissionId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getFormAnswers', 'clientId', clientId)
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('getFormAnswers', 'formId', formId)
            const localVarPath = `/api/v1/clients/{clientId}/forms/{formId}/answers`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (submissionId !== undefined) {
                localVarQueryParameter['submissionId'] = submissionId;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the value of an existing answer. Requires providing a reason for the audit trail. The previous value is stored in the audit history. 
         * @summary Update an existing answer
         * @param {string} answerId Answer UUID
         * @param {UpdateAnswerRequest} updateAnswerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAnswer: async (answerId: string, updateAnswerRequest: UpdateAnswerRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'answerId' is not null or undefined
            assertParamExists('updateAnswer', 'answerId', answerId)
            // verify required parameter 'updateAnswerRequest' is not null or undefined
            assertParamExists('updateAnswer', 'updateAnswerRequest', updateAnswerRequest)
            const localVarPath = `/api/v1/answers/{answerId}`
                .replace(`{${"answerId"}}`, encodeURIComponent(String(answerId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAnswerRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AnswersApi - functional programming interface
 */
export const AnswersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AnswersApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns a single answer with its current value.
         * @summary Get an answer by ID
         * @param {string} answerId Answer UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAnswer(answerId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AnswerResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAnswer(answerId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AnswersApi.getAnswer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all immutable versions of an answer ordered by version number descending. Each version captures the answer value, the form submission status at the time of save, and the actor who saved it. The current version (highest number) reflects the active value on the answer. 
         * @summary Get version history for an answer
         * @param {string} answerId Answer UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAnswerVersions(answerId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AnswerVersionsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAnswerVersions(answerId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AnswersApi.getAnswerVersions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all answers submitted for a specific form by a client. Includes the most recent submission if available. 
         * @summary Get all answers for a form
         * @param {string} clientId Client UUID
         * @param {string} formId Form UUID
         * @param {string} [submissionId] Filter by specific submission ID (optional)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFormAnswers(clientId: string, formId: string, submissionId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormAnswersResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormAnswers(clientId, formId, submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AnswersApi.getFormAnswers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the value of an existing answer. Requires providing a reason for the audit trail. The previous value is stored in the audit history. 
         * @summary Update an existing answer
         * @param {string} answerId Answer UUID
         * @param {UpdateAnswerRequest} updateAnswerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAnswer(answerId: string, updateAnswerRequest: UpdateAnswerRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AnswerResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAnswer(answerId, updateAnswerRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AnswersApi.updateAnswer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AnswersApi - factory interface
 */
export const AnswersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AnswersApiFp(configuration)
    return {
        /**
         * Returns a single answer with its current value.
         * @summary Get an answer by ID
         * @param {string} answerId Answer UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAnswer(answerId: string, options?: RawAxiosRequestConfig): AxiosPromise<AnswerResponse> {
            return localVarFp.getAnswer(answerId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all immutable versions of an answer ordered by version number descending. Each version captures the answer value, the form submission status at the time of save, and the actor who saved it. The current version (highest number) reflects the active value on the answer. 
         * @summary Get version history for an answer
         * @param {string} answerId Answer UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAnswerVersions(answerId: string, options?: RawAxiosRequestConfig): AxiosPromise<AnswerVersionsResponse> {
            return localVarFp.getAnswerVersions(answerId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all answers submitted for a specific form by a client. Includes the most recent submission if available. 
         * @summary Get all answers for a form
         * @param {string} clientId Client UUID
         * @param {string} formId Form UUID
         * @param {string} [submissionId] Filter by specific submission ID (optional)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormAnswers(clientId: string, formId: string, submissionId?: string, options?: RawAxiosRequestConfig): AxiosPromise<FormAnswersResponse> {
            return localVarFp.getFormAnswers(clientId, formId, submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the value of an existing answer. Requires providing a reason for the audit trail. The previous value is stored in the audit history. 
         * @summary Update an existing answer
         * @param {string} answerId Answer UUID
         * @param {UpdateAnswerRequest} updateAnswerRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAnswer(answerId: string, updateAnswerRequest: UpdateAnswerRequest, options?: RawAxiosRequestConfig): AxiosPromise<AnswerResponse> {
            return localVarFp.updateAnswer(answerId, updateAnswerRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AnswersApi - object-oriented interface
 */
export class AnswersApi extends BaseAPI {
    /**
     * Returns a single answer with its current value.
     * @summary Get an answer by ID
     * @param {string} answerId Answer UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAnswer(answerId: string, options?: RawAxiosRequestConfig) {
        return AnswersApiFp(this.configuration).getAnswer(answerId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all immutable versions of an answer ordered by version number descending. Each version captures the answer value, the form submission status at the time of save, and the actor who saved it. The current version (highest number) reflects the active value on the answer. 
     * @summary Get version history for an answer
     * @param {string} answerId Answer UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAnswerVersions(answerId: string, options?: RawAxiosRequestConfig) {
        return AnswersApiFp(this.configuration).getAnswerVersions(answerId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all answers submitted for a specific form by a client. Includes the most recent submission if available. 
     * @summary Get all answers for a form
     * @param {string} clientId Client UUID
     * @param {string} formId Form UUID
     * @param {string} [submissionId] Filter by specific submission ID (optional)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFormAnswers(clientId: string, formId: string, submissionId?: string, options?: RawAxiosRequestConfig) {
        return AnswersApiFp(this.configuration).getFormAnswers(clientId, formId, submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the value of an existing answer. Requires providing a reason for the audit trail. The previous value is stored in the audit history. 
     * @summary Update an existing answer
     * @param {string} answerId Answer UUID
     * @param {UpdateAnswerRequest} updateAnswerRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAnswer(answerId: string, updateAnswerRequest: UpdateAnswerRequest, options?: RawAxiosRequestConfig) {
        return AnswersApiFp(this.configuration).updateAnswer(answerId, updateAnswerRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AppointmentFormsApi - axios parameter creator
 */
export const AppointmentFormsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns a completion matrix showing which clients have completed which forms for a group appointment. This enables seeing progress like \"3 of 5 clients completed\" for each form.  For single-client appointments, returns the status for that single client. For group appointments, returns status for all participating clients.  The response includes: - Individual status for each client-form combination (PENDING or SUBMITTED) - Progress per form (how many clients have completed it) - Overall statistics (total forms, total clients) 
         * @summary Get form completion status by client
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormStatusByClient: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getFormStatusByClient', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/form-status`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns forms that need to be completed after an appointment, based on the appointment type\'s linked forms. Shows which forms are already filled and which are still pending. 
         * @summary Get forms needing completion after appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPendingForms: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('getPendingForms', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/pending-forms`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all form submissions for an appointment, grouped by client. For single-client appointments, returns a single client group. For group appointments, returns submissions organized per client.  Submissions are ordered by submission time descending (most recent first) within each client group. 
         * @summary List form submissions for an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentFormSubmissions: async (appointmentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentId' is not null or undefined
            assertParamExists('listAppointmentFormSubmissions', 'appointmentId', appointmentId)
            const localVarPath = `/api/v1/appointments/{appointmentId}/form-submissions`
                .replace(`{${"appointmentId"}}`, encodeURIComponent(String(appointmentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentFormsApi - functional programming interface
 */
export const AppointmentFormsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentFormsApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns a completion matrix showing which clients have completed which forms for a group appointment. This enables seeing progress like \"3 of 5 clients completed\" for each form.  For single-client appointments, returns the status for that single client. For group appointments, returns status for all participating clients.  The response includes: - Individual status for each client-form combination (PENDING or SUBMITTED) - Progress per form (how many clients have completed it) - Overall statistics (total forms, total clients) 
         * @summary Get form completion status by client
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFormStatusByClient(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentFormStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormStatusByClient(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentFormsApi.getFormStatusByClient']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns forms that need to be completed after an appointment, based on the appointment type\'s linked forms. Shows which forms are already filled and which are still pending. 
         * @summary Get forms needing completion after appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPendingForms(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PendingFormsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPendingForms(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentFormsApi.getPendingForms']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all form submissions for an appointment, grouped by client. For single-client appointments, returns a single client group. For group appointments, returns submissions organized per client.  Submissions are ordered by submission time descending (most recent first) within each client group. 
         * @summary List form submissions for an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointmentFormSubmissions(appointmentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentFormSubmissionsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointmentFormSubmissions(appointmentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentFormsApi.listAppointmentFormSubmissions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentFormsApi - factory interface
 */
export const AppointmentFormsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentFormsApiFp(configuration)
    return {
        /**
         * Returns a completion matrix showing which clients have completed which forms for a group appointment. This enables seeing progress like \"3 of 5 clients completed\" for each form.  For single-client appointments, returns the status for that single client. For group appointments, returns status for all participating clients.  The response includes: - Individual status for each client-form combination (PENDING or SUBMITTED) - Progress per form (how many clients have completed it) - Overall statistics (total forms, total clients) 
         * @summary Get form completion status by client
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormStatusByClient(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentFormStatusResponse> {
            return localVarFp.getFormStatusByClient(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns forms that need to be completed after an appointment, based on the appointment type\'s linked forms. Shows which forms are already filled and which are still pending. 
         * @summary Get forms needing completion after appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPendingForms(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<PendingFormsResponse> {
            return localVarFp.getPendingForms(appointmentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all form submissions for an appointment, grouped by client. For single-client appointments, returns a single client group. For group appointments, returns submissions organized per client.  Submissions are ordered by submission time descending (most recent first) within each client group. 
         * @summary List form submissions for an appointment
         * @param {string} appointmentId Appointment UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentFormSubmissions(appointmentId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentFormSubmissionsResponse> {
            return localVarFp.listAppointmentFormSubmissions(appointmentId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentFormsApi - object-oriented interface
 */
export class AppointmentFormsApi extends BaseAPI {
    /**
     * Returns a completion matrix showing which clients have completed which forms for a group appointment. This enables seeing progress like \"3 of 5 clients completed\" for each form.  For single-client appointments, returns the status for that single client. For group appointments, returns status for all participating clients.  The response includes: - Individual status for each client-form combination (PENDING or SUBMITTED) - Progress per form (how many clients have completed it) - Overall statistics (total forms, total clients) 
     * @summary Get form completion status by client
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFormStatusByClient(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentFormsApiFp(this.configuration).getFormStatusByClient(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns forms that need to be completed after an appointment, based on the appointment type\'s linked forms. Shows which forms are already filled and which are still pending. 
     * @summary Get forms needing completion after appointment
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPendingForms(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentFormsApiFp(this.configuration).getPendingForms(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all form submissions for an appointment, grouped by client. For single-client appointments, returns a single client group. For group appointments, returns submissions organized per client.  Submissions are ordered by submission time descending (most recent first) within each client group. 
     * @summary List form submissions for an appointment
     * @param {string} appointmentId Appointment UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAppointmentFormSubmissions(appointmentId: string, options?: RawAxiosRequestConfig) {
        return AppointmentFormsApiFp(this.configuration).listAppointmentFormSubmissions(appointmentId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AppointmentTypeFormsApi - axios parameter creator
 */
export const AppointmentTypeFormsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Links a form to an appointment type. The form will be presented to behandelaren after appointments of this type are completed. 
         * @summary Link a form to appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {LinkFormToAppointmentTypeRequest} linkFormToAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        linkFormToAppointmentType: async (appointmentTypeId: string, linkFormToAppointmentTypeRequest: LinkFormToAppointmentTypeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('linkFormToAppointmentType', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'linkFormToAppointmentTypeRequest' is not null or undefined
            assertParamExists('linkFormToAppointmentType', 'linkFormToAppointmentTypeRequest', linkFormToAppointmentTypeRequest)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/forms`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(linkFormToAppointmentTypeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all forms linked to a specific appointment type, ordered by display order. These forms are presented to behandelaren after appointments of this type. 
         * @summary List forms linked to appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentTypeForms: async (appointmentTypeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('listAppointmentTypeForms', 'appointmentTypeId', appointmentTypeId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/forms`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the link between a form and an appointment type. This does not delete the form itself. 
         * @summary Unlink a form from appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unlinkFormFromAppointmentType: async (appointmentTypeId: string, formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('unlinkFormFromAppointmentType', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('unlinkFormFromAppointmentType', 'formId', formId)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/forms/{formId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)))
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the link settings between a form and an appointment type, such as display order and required status. 
         * @summary Update form link settings
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {string} formId Form UUID
         * @param {UpdateAppointmentTypeFormLinkRequest} updateAppointmentTypeFormLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentTypeFormLink: async (appointmentTypeId: string, formId: string, updateAppointmentTypeFormLinkRequest: UpdateAppointmentTypeFormLinkRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'appointmentTypeId' is not null or undefined
            assertParamExists('updateAppointmentTypeFormLink', 'appointmentTypeId', appointmentTypeId)
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('updateAppointmentTypeFormLink', 'formId', formId)
            // verify required parameter 'updateAppointmentTypeFormLinkRequest' is not null or undefined
            assertParamExists('updateAppointmentTypeFormLink', 'updateAppointmentTypeFormLinkRequest', updateAppointmentTypeFormLinkRequest)
            const localVarPath = `/api/v1/appointment-types/{appointmentTypeId}/forms/{formId}`
                .replace(`{${"appointmentTypeId"}}`, encodeURIComponent(String(appointmentTypeId)))
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateAppointmentTypeFormLinkRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AppointmentTypeFormsApi - functional programming interface
 */
export const AppointmentTypeFormsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AppointmentTypeFormsApiAxiosParamCreator(configuration)
    return {
        /**
         * Links a form to an appointment type. The form will be presented to behandelaren after appointments of this type are completed. 
         * @summary Link a form to appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {LinkFormToAppointmentTypeRequest} linkFormToAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async linkFormToAppointmentType(appointmentTypeId: string, linkFormToAppointmentTypeRequest: LinkFormToAppointmentTypeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeFormLinkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.linkFormToAppointmentType(appointmentTypeId, linkFormToAppointmentTypeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypeFormsApi.linkFormToAppointmentType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all forms linked to a specific appointment type, ordered by display order. These forms are presented to behandelaren after appointments of this type. 
         * @summary List forms linked to appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listAppointmentTypeForms(appointmentTypeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeFormLinkListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listAppointmentTypeForms(appointmentTypeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypeFormsApi.listAppointmentTypeForms']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the link between a form and an appointment type. This does not delete the form itself. 
         * @summary Unlink a form from appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unlinkFormFromAppointmentType(appointmentTypeId: string, formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unlinkFormFromAppointmentType(appointmentTypeId, formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypeFormsApi.unlinkFormFromAppointmentType']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the link settings between a form and an appointment type, such as display order and required status. 
         * @summary Update form link settings
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {string} formId Form UUID
         * @param {UpdateAppointmentTypeFormLinkRequest} updateAppointmentTypeFormLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateAppointmentTypeFormLink(appointmentTypeId: string, formId: string, updateAppointmentTypeFormLinkRequest: UpdateAppointmentTypeFormLinkRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AppointmentTypeFormLinkResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateAppointmentTypeFormLink(appointmentTypeId, formId, updateAppointmentTypeFormLinkRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AppointmentTypeFormsApi.updateAppointmentTypeFormLink']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AppointmentTypeFormsApi - factory interface
 */
export const AppointmentTypeFormsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AppointmentTypeFormsApiFp(configuration)
    return {
        /**
         * Links a form to an appointment type. The form will be presented to behandelaren after appointments of this type are completed. 
         * @summary Link a form to appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {LinkFormToAppointmentTypeRequest} linkFormToAppointmentTypeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        linkFormToAppointmentType(appointmentTypeId: string, linkFormToAppointmentTypeRequest: LinkFormToAppointmentTypeRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeFormLinkResponse> {
            return localVarFp.linkFormToAppointmentType(appointmentTypeId, linkFormToAppointmentTypeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all forms linked to a specific appointment type, ordered by display order. These forms are presented to behandelaren after appointments of this type. 
         * @summary List forms linked to appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listAppointmentTypeForms(appointmentTypeId: string, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeFormLinkListResponse> {
            return localVarFp.listAppointmentTypeForms(appointmentTypeId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the link between a form and an appointment type. This does not delete the form itself. 
         * @summary Unlink a form from appointment type
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unlinkFormFromAppointmentType(appointmentTypeId: string, formId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.unlinkFormFromAppointmentType(appointmentTypeId, formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the link settings between a form and an appointment type, such as display order and required status. 
         * @summary Update form link settings
         * @param {string} appointmentTypeId Appointment Type UUID
         * @param {string} formId Form UUID
         * @param {UpdateAppointmentTypeFormLinkRequest} updateAppointmentTypeFormLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateAppointmentTypeFormLink(appointmentTypeId: string, formId: string, updateAppointmentTypeFormLinkRequest: UpdateAppointmentTypeFormLinkRequest, options?: RawAxiosRequestConfig): AxiosPromise<AppointmentTypeFormLinkResponse> {
            return localVarFp.updateAppointmentTypeFormLink(appointmentTypeId, formId, updateAppointmentTypeFormLinkRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AppointmentTypeFormsApi - object-oriented interface
 */
export class AppointmentTypeFormsApi extends BaseAPI {
    /**
     * Links a form to an appointment type. The form will be presented to behandelaren after appointments of this type are completed. 
     * @summary Link a form to appointment type
     * @param {string} appointmentTypeId Appointment Type UUID
     * @param {LinkFormToAppointmentTypeRequest} linkFormToAppointmentTypeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public linkFormToAppointmentType(appointmentTypeId: string, linkFormToAppointmentTypeRequest: LinkFormToAppointmentTypeRequest, options?: RawAxiosRequestConfig) {
        return AppointmentTypeFormsApiFp(this.configuration).linkFormToAppointmentType(appointmentTypeId, linkFormToAppointmentTypeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all forms linked to a specific appointment type, ordered by display order. These forms are presented to behandelaren after appointments of this type. 
     * @summary List forms linked to appointment type
     * @param {string} appointmentTypeId Appointment Type UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listAppointmentTypeForms(appointmentTypeId: string, options?: RawAxiosRequestConfig) {
        return AppointmentTypeFormsApiFp(this.configuration).listAppointmentTypeForms(appointmentTypeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the link between a form and an appointment type. This does not delete the form itself. 
     * @summary Unlink a form from appointment type
     * @param {string} appointmentTypeId Appointment Type UUID
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unlinkFormFromAppointmentType(appointmentTypeId: string, formId: string, options?: RawAxiosRequestConfig) {
        return AppointmentTypeFormsApiFp(this.configuration).unlinkFormFromAppointmentType(appointmentTypeId, formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the link settings between a form and an appointment type, such as display order and required status. 
     * @summary Update form link settings
     * @param {string} appointmentTypeId Appointment Type UUID
     * @param {string} formId Form UUID
     * @param {UpdateAppointmentTypeFormLinkRequest} updateAppointmentTypeFormLinkRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateAppointmentTypeFormLink(appointmentTypeId: string, formId: string, updateAppointmentTypeFormLinkRequest: UpdateAppointmentTypeFormLinkRequest, options?: RawAxiosRequestConfig) {
        return AppointmentTypeFormsApiFp(this.configuration).updateAppointmentTypeFormLink(appointmentTypeId, formId, updateAppointmentTypeFormLinkRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FormItemsApi - axios parameter creator
 */
export const FormItemsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Adds a new item (question, static text, or document link) to a form. Only forms in DRAFT status can have items added. 
         * @summary Add an item to a form
         * @param {string} formId Form UUID
         * @param {AddFormItemRequest} addFormItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addFormItem: async (formId: string, addFormItemRequest: AddFormItemRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('addFormItem', 'formId', formId)
            // verify required parameter 'addFormItemRequest' is not null or undefined
            assertParamExists('addFormItem', 'addFormItemRequest', addFormItemRequest)
            const localVarPath = `/api/v1/forms/{formId}/items`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(addFormItemRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes an item from a form. Only forms in DRAFT status can have items removed. 
         * @summary Remove an item from a form
         * @param {string} formId Form UUID
         * @param {string} itemId Form Item UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteFormItem: async (formId: string, itemId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('deleteFormItem', 'formId', formId)
            // verify required parameter 'itemId' is not null or undefined
            assertParamExists('deleteFormItem', 'itemId', itemId)
            const localVarPath = `/api/v1/forms/{formId}/items/{itemId}`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)))
                .replace(`{${"itemId"}}`, encodeURIComponent(String(itemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a copy of a form item and inserts it directly after the original. For question items, a new question is created with the same properties. The duplicated question will not be added to the library. Only forms in DRAFT or ACTIVE status can have items duplicated. 
         * @summary Duplicate a form item
         * @param {string} formId Form UUID
         * @param {string} itemId Form Item UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        duplicateFormItem: async (formId: string, itemId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('duplicateFormItem', 'formId', formId)
            // verify required parameter 'itemId' is not null or undefined
            assertParamExists('duplicateFormItem', 'itemId', itemId)
            const localVarPath = `/api/v1/forms/{formId}/items/{itemId}/duplicate`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)))
                .replace(`{${"itemId"}}`, encodeURIComponent(String(itemId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reorders all items in a form by providing a new ordered list of item IDs. Only forms in DRAFT status can have items reordered. 
         * @summary Reorder form items
         * @param {string} formId Form UUID
         * @param {ReorderItemsRequest} reorderItemsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reorderFormItems: async (formId: string, reorderItemsRequest: ReorderItemsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('reorderFormItems', 'formId', formId)
            // verify required parameter 'reorderItemsRequest' is not null or undefined
            assertParamExists('reorderFormItems', 'reorderItemsRequest', reorderItemsRequest)
            const localVarPath = `/api/v1/forms/{formId}/items/reorder`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(reorderItemsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FormItemsApi - functional programming interface
 */
export const FormItemsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FormItemsApiAxiosParamCreator(configuration)
    return {
        /**
         * Adds a new item (question, static text, or document link) to a form. Only forms in DRAFT status can have items added. 
         * @summary Add an item to a form
         * @param {string} formId Form UUID
         * @param {AddFormItemRequest} addFormItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addFormItem(formId: string, addFormItemRequest: AddFormItemRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormItemResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addFormItem(formId, addFormItemRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormItemsApi.addFormItem']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes an item from a form. Only forms in DRAFT status can have items removed. 
         * @summary Remove an item from a form
         * @param {string} formId Form UUID
         * @param {string} itemId Form Item UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteFormItem(formId: string, itemId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteFormItem(formId, itemId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormItemsApi.deleteFormItem']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a copy of a form item and inserts it directly after the original. For question items, a new question is created with the same properties. The duplicated question will not be added to the library. Only forms in DRAFT or ACTIVE status can have items duplicated. 
         * @summary Duplicate a form item
         * @param {string} formId Form UUID
         * @param {string} itemId Form Item UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async duplicateFormItem(formId: string, itemId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.duplicateFormItem(formId, itemId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormItemsApi.duplicateFormItem']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reorders all items in a form by providing a new ordered list of item IDs. Only forms in DRAFT status can have items reordered. 
         * @summary Reorder form items
         * @param {string} formId Form UUID
         * @param {ReorderItemsRequest} reorderItemsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reorderFormItems(formId: string, reorderItemsRequest: ReorderItemsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reorderFormItems(formId, reorderItemsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormItemsApi.reorderFormItems']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FormItemsApi - factory interface
 */
export const FormItemsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FormItemsApiFp(configuration)
    return {
        /**
         * Adds a new item (question, static text, or document link) to a form. Only forms in DRAFT status can have items added. 
         * @summary Add an item to a form
         * @param {string} formId Form UUID
         * @param {AddFormItemRequest} addFormItemRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addFormItem(formId: string, addFormItemRequest: AddFormItemRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormItemResponse> {
            return localVarFp.addFormItem(formId, addFormItemRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes an item from a form. Only forms in DRAFT status can have items removed. 
         * @summary Remove an item from a form
         * @param {string} formId Form UUID
         * @param {string} itemId Form Item UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteFormItem(formId: string, itemId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteFormItem(formId, itemId, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a copy of a form item and inserts it directly after the original. For question items, a new question is created with the same properties. The duplicated question will not be added to the library. Only forms in DRAFT or ACTIVE status can have items duplicated. 
         * @summary Duplicate a form item
         * @param {string} formId Form UUID
         * @param {string} itemId Form Item UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        duplicateFormItem(formId: string, itemId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.duplicateFormItem(formId, itemId, options).then((request) => request(axios, basePath));
        },
        /**
         * Reorders all items in a form by providing a new ordered list of item IDs. Only forms in DRAFT status can have items reordered. 
         * @summary Reorder form items
         * @param {string} formId Form UUID
         * @param {ReorderItemsRequest} reorderItemsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reorderFormItems(formId: string, reorderItemsRequest: ReorderItemsRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.reorderFormItems(formId, reorderItemsRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FormItemsApi - object-oriented interface
 */
export class FormItemsApi extends BaseAPI {
    /**
     * Adds a new item (question, static text, or document link) to a form. Only forms in DRAFT status can have items added. 
     * @summary Add an item to a form
     * @param {string} formId Form UUID
     * @param {AddFormItemRequest} addFormItemRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addFormItem(formId: string, addFormItemRequest: AddFormItemRequest, options?: RawAxiosRequestConfig) {
        return FormItemsApiFp(this.configuration).addFormItem(formId, addFormItemRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes an item from a form. Only forms in DRAFT status can have items removed. 
     * @summary Remove an item from a form
     * @param {string} formId Form UUID
     * @param {string} itemId Form Item UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteFormItem(formId: string, itemId: string, options?: RawAxiosRequestConfig) {
        return FormItemsApiFp(this.configuration).deleteFormItem(formId, itemId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a copy of a form item and inserts it directly after the original. For question items, a new question is created with the same properties. The duplicated question will not be added to the library. Only forms in DRAFT or ACTIVE status can have items duplicated. 
     * @summary Duplicate a form item
     * @param {string} formId Form UUID
     * @param {string} itemId Form Item UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public duplicateFormItem(formId: string, itemId: string, options?: RawAxiosRequestConfig) {
        return FormItemsApiFp(this.configuration).duplicateFormItem(formId, itemId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reorders all items in a form by providing a new ordered list of item IDs. Only forms in DRAFT status can have items reordered. 
     * @summary Reorder form items
     * @param {string} formId Form UUID
     * @param {ReorderItemsRequest} reorderItemsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reorderFormItems(formId: string, reorderItemsRequest: ReorderItemsRequest, options?: RawAxiosRequestConfig) {
        return FormItemsApiFp(this.configuration).reorderFormItems(formId, reorderItemsRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FormPdfApi - axios parameter creator
 */
export const FormPdfApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the form as an A4 print layout to be completed by hand: every question is followed by writing space, and the choice questions (SINGLE_CHOICE, MULTI_CHOICE, YES_NO, AGREEMENT) by checkboxes to tick with a pen.  This is a print layout, not an interactive (AcroForm) PDF. 
         * @summary Download a blank printable form PDF
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBlankFormPdf: async (formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('downloadBlankFormPdf', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}/pdf`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the submission as an A4 print layout: every question followed by the answer that was given. Questions left unanswered are printed as such.  A dossiernotitie (the `DOSSIERNOTITIE` system form) is printed under the title \"Dossiernotitie – {afspraak} – {datum}\", with the afspraak, the cliënt and the author under it. 
         * @summary Download a filled form submission as PDF
         * @param {string} submissionId Form Submission UUID
         * @param {string} [clientId] The cliënt whose dossier the print is for, named on it. It must be a cliënt the submission belongs to: its own cliënt, or — for a submission filed on an afspraak for no cliënt, such as a dossiernotitie of a group session — a participant of that afspraak. Any other cliënt is a 400. Without it, the submission\&#39;s own cliënt is named, and a submission without one names none. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadFormSubmissionPdf: async (submissionId: string, clientId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('downloadFormSubmissionPdf', 'submissionId', submissionId)
            const localVarPath = `/api/v1/form-submissions/{submissionId}/pdf`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FormPdfApi - functional programming interface
 */
export const FormPdfApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FormPdfApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the form as an A4 print layout to be completed by hand: every question is followed by writing space, and the choice questions (SINGLE_CHOICE, MULTI_CHOICE, YES_NO, AGREEMENT) by checkboxes to tick with a pen.  This is a print layout, not an interactive (AcroForm) PDF. 
         * @summary Download a blank printable form PDF
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadBlankFormPdf(formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadBlankFormPdf(formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormPdfApi.downloadBlankFormPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the submission as an A4 print layout: every question followed by the answer that was given. Questions left unanswered are printed as such.  A dossiernotitie (the `DOSSIERNOTITIE` system form) is printed under the title \"Dossiernotitie – {afspraak} – {datum}\", with the afspraak, the cliënt and the author under it. 
         * @summary Download a filled form submission as PDF
         * @param {string} submissionId Form Submission UUID
         * @param {string} [clientId] The cliënt whose dossier the print is for, named on it. It must be a cliënt the submission belongs to: its own cliënt, or — for a submission filed on an afspraak for no cliënt, such as a dossiernotitie of a group session — a participant of that afspraak. Any other cliënt is a 400. Without it, the submission\&#39;s own cliënt is named, and a submission without one names none. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadFormSubmissionPdf(submissionId: string, clientId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadFormSubmissionPdf(submissionId, clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormPdfApi.downloadFormSubmissionPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FormPdfApi - factory interface
 */
export const FormPdfApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FormPdfApiFp(configuration)
    return {
        /**
         * Returns the form as an A4 print layout to be completed by hand: every question is followed by writing space, and the choice questions (SINGLE_CHOICE, MULTI_CHOICE, YES_NO, AGREEMENT) by checkboxes to tick with a pen.  This is a print layout, not an interactive (AcroForm) PDF. 
         * @summary Download a blank printable form PDF
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBlankFormPdf(formId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadBlankFormPdf(formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the submission as an A4 print layout: every question followed by the answer that was given. Questions left unanswered are printed as such.  A dossiernotitie (the `DOSSIERNOTITIE` system form) is printed under the title \"Dossiernotitie – {afspraak} – {datum}\", with the afspraak, the cliënt and the author under it. 
         * @summary Download a filled form submission as PDF
         * @param {string} submissionId Form Submission UUID
         * @param {string} [clientId] The cliënt whose dossier the print is for, named on it. It must be a cliënt the submission belongs to: its own cliënt, or — for a submission filed on an afspraak for no cliënt, such as a dossiernotitie of a group session — a participant of that afspraak. Any other cliënt is a 400. Without it, the submission\&#39;s own cliënt is named, and a submission without one names none. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadFormSubmissionPdf(submissionId: string, clientId?: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadFormSubmissionPdf(submissionId, clientId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FormPdfApi - object-oriented interface
 */
export class FormPdfApi extends BaseAPI {
    /**
     * Returns the form as an A4 print layout to be completed by hand: every question is followed by writing space, and the choice questions (SINGLE_CHOICE, MULTI_CHOICE, YES_NO, AGREEMENT) by checkboxes to tick with a pen.  This is a print layout, not an interactive (AcroForm) PDF. 
     * @summary Download a blank printable form PDF
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadBlankFormPdf(formId: string, options?: RawAxiosRequestConfig) {
        return FormPdfApiFp(this.configuration).downloadBlankFormPdf(formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the submission as an A4 print layout: every question followed by the answer that was given. Questions left unanswered are printed as such.  A dossiernotitie (the `DOSSIERNOTITIE` system form) is printed under the title \"Dossiernotitie – {afspraak} – {datum}\", with the afspraak, the cliënt and the author under it. 
     * @summary Download a filled form submission as PDF
     * @param {string} submissionId Form Submission UUID
     * @param {string} [clientId] The cliënt whose dossier the print is for, named on it. It must be a cliënt the submission belongs to: its own cliënt, or — for a submission filed on an afspraak for no cliënt, such as a dossiernotitie of a group session — a participant of that afspraak. Any other cliënt is a 400. Without it, the submission\&#39;s own cliënt is named, and a submission without one names none. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadFormSubmissionPdf(submissionId: string, clientId?: string, options?: RawAxiosRequestConfig) {
        return FormPdfApiFp(this.configuration).downloadFormSubmissionPdf(submissionId, clientId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FormSubmissionAnswersApi - axios parameter creator
 */
export const FormSubmissionAnswersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Generates AI-powered answer suggestions for a submission based on associated transcriptions. Does NOT save the answers - returns suggestions for user review.  The endpoint gathers all completed transcriptions linked to the submission: - If submission has appointmentId: gets transcripts via appointment - Otherwise: gets transcripts via TranscriptionSessions linked to formSubmissionId  Only transcriptions with status = COMPLETED are used. Returns an empty array if no completed transcriptions are available.  The AI analyzes the transcript text and generates suggested answers for each question. The frontend can display these suggestions in form fields for user review. The user must click \"Save\" to persist the suggestions as actual answers. 
         * @summary Generate AI suggestions for form answers
         * @param {string} submissionId Form submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateAiSuggestions: async (submissionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('generateAiSuggestions', 'submissionId', submissionId)
            const localVarPath = `/api/v1/form-submissions/{submissionId}/ai-suggestions`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Marks a form submission as reviewed. Only submissions in DRAFT status can be marked as reviewed. The status change is audited. 
         * @summary Mark form submission as reviewed
         * @param {string} submissionId Form submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markFormSubmissionAsReviewed: async (submissionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('markFormSubmissionAsReviewed', 'submissionId', submissionId)
            const localVarPath = `/api/v1/form-submissions/{submissionId}/mark-reviewed`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Saves multiple answers in a single transaction. Creates new answer versions for each answer. If an answer doesn\'t exist yet, creates it.  Through the public API only a draft can be filled in: the answers are a proposal a behandelaar reviews in Scrivio, so a submission that has already been reviewed is refused with a 409. 
         * @summary Save answers for a form submission
         * @param {string} submissionId Form submission UUID
         * @param {SaveAnswersRequest} saveAnswersRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveFormSubmissionAnswers: async (submissionId: string, saveAnswersRequest: SaveAnswersRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('saveFormSubmissionAnswers', 'submissionId', submissionId)
            // verify required parameter 'saveAnswersRequest' is not null or undefined
            assertParamExists('saveFormSubmissionAnswers', 'saveAnswersRequest', saveAnswersRequest)
            const localVarPath = `/api/v1/form-submissions/{submissionId}/answers`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(saveAnswersRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FormSubmissionAnswersApi - functional programming interface
 */
export const FormSubmissionAnswersApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FormSubmissionAnswersApiAxiosParamCreator(configuration)
    return {
        /**
         * Generates AI-powered answer suggestions for a submission based on associated transcriptions. Does NOT save the answers - returns suggestions for user review.  The endpoint gathers all completed transcriptions linked to the submission: - If submission has appointmentId: gets transcripts via appointment - Otherwise: gets transcripts via TranscriptionSessions linked to formSubmissionId  Only transcriptions with status = COMPLETED are used. Returns an empty array if no completed transcriptions are available.  The AI analyzes the transcript text and generates suggested answers for each question. The frontend can display these suggestions in form fields for user review. The user must click \"Save\" to persist the suggestions as actual answers. 
         * @summary Generate AI suggestions for form answers
         * @param {string} submissionId Form submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateAiSuggestions(submissionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AiSuggestionsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateAiSuggestions(submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionAnswersApi.generateAiSuggestions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Marks a form submission as reviewed. Only submissions in DRAFT status can be marked as reviewed. The status change is audited. 
         * @summary Mark form submission as reviewed
         * @param {string} submissionId Form submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async markFormSubmissionAsReviewed(submissionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.markFormSubmissionAsReviewed(submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionAnswersApi.markFormSubmissionAsReviewed']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Saves multiple answers in a single transaction. Creates new answer versions for each answer. If an answer doesn\'t exist yet, creates it.  Through the public API only a draft can be filled in: the answers are a proposal a behandelaar reviews in Scrivio, so a submission that has already been reviewed is refused with a 409. 
         * @summary Save answers for a form submission
         * @param {string} submissionId Form submission UUID
         * @param {SaveAnswersRequest} saveAnswersRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async saveFormSubmissionAnswers(submissionId: string, saveAnswersRequest: SaveAnswersRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.saveFormSubmissionAnswers(submissionId, saveAnswersRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionAnswersApi.saveFormSubmissionAnswers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FormSubmissionAnswersApi - factory interface
 */
export const FormSubmissionAnswersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FormSubmissionAnswersApiFp(configuration)
    return {
        /**
         * Generates AI-powered answer suggestions for a submission based on associated transcriptions. Does NOT save the answers - returns suggestions for user review.  The endpoint gathers all completed transcriptions linked to the submission: - If submission has appointmentId: gets transcripts via appointment - Otherwise: gets transcripts via TranscriptionSessions linked to formSubmissionId  Only transcriptions with status = COMPLETED are used. Returns an empty array if no completed transcriptions are available.  The AI analyzes the transcript text and generates suggested answers for each question. The frontend can display these suggestions in form fields for user review. The user must click \"Save\" to persist the suggestions as actual answers. 
         * @summary Generate AI suggestions for form answers
         * @param {string} submissionId Form submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateAiSuggestions(submissionId: string, options?: RawAxiosRequestConfig): AxiosPromise<AiSuggestionsResponse> {
            return localVarFp.generateAiSuggestions(submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Marks a form submission as reviewed. Only submissions in DRAFT status can be marked as reviewed. The status change is audited. 
         * @summary Mark form submission as reviewed
         * @param {string} submissionId Form submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        markFormSubmissionAsReviewed(submissionId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionDetailResponse> {
            return localVarFp.markFormSubmissionAsReviewed(submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Saves multiple answers in a single transaction. Creates new answer versions for each answer. If an answer doesn\'t exist yet, creates it.  Through the public API only a draft can be filled in: the answers are a proposal a behandelaar reviews in Scrivio, so a submission that has already been reviewed is refused with a 409. 
         * @summary Save answers for a form submission
         * @param {string} submissionId Form submission UUID
         * @param {SaveAnswersRequest} saveAnswersRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        saveFormSubmissionAnswers(submissionId: string, saveAnswersRequest: SaveAnswersRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionDetailResponse> {
            return localVarFp.saveFormSubmissionAnswers(submissionId, saveAnswersRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FormSubmissionAnswersApi - object-oriented interface
 */
export class FormSubmissionAnswersApi extends BaseAPI {
    /**
     * Generates AI-powered answer suggestions for a submission based on associated transcriptions. Does NOT save the answers - returns suggestions for user review.  The endpoint gathers all completed transcriptions linked to the submission: - If submission has appointmentId: gets transcripts via appointment - Otherwise: gets transcripts via TranscriptionSessions linked to formSubmissionId  Only transcriptions with status = COMPLETED are used. Returns an empty array if no completed transcriptions are available.  The AI analyzes the transcript text and generates suggested answers for each question. The frontend can display these suggestions in form fields for user review. The user must click \"Save\" to persist the suggestions as actual answers. 
     * @summary Generate AI suggestions for form answers
     * @param {string} submissionId Form submission UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateAiSuggestions(submissionId: string, options?: RawAxiosRequestConfig) {
        return FormSubmissionAnswersApiFp(this.configuration).generateAiSuggestions(submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Marks a form submission as reviewed. Only submissions in DRAFT status can be marked as reviewed. The status change is audited. 
     * @summary Mark form submission as reviewed
     * @param {string} submissionId Form submission UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public markFormSubmissionAsReviewed(submissionId: string, options?: RawAxiosRequestConfig) {
        return FormSubmissionAnswersApiFp(this.configuration).markFormSubmissionAsReviewed(submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Saves multiple answers in a single transaction. Creates new answer versions for each answer. If an answer doesn\'t exist yet, creates it.  Through the public API only a draft can be filled in: the answers are a proposal a behandelaar reviews in Scrivio, so a submission that has already been reviewed is refused with a 409. 
     * @summary Save answers for a form submission
     * @param {string} submissionId Form submission UUID
     * @param {SaveAnswersRequest} saveAnswersRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public saveFormSubmissionAnswers(submissionId: string, saveAnswersRequest: SaveAnswersRequest, options?: RawAxiosRequestConfig) {
        return FormSubmissionAnswersApiFp(this.configuration).saveFormSubmissionAnswers(submissionId, saveAnswersRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FormSubmissionsApi - axios parameter creator
 */
export const FormSubmissionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates a new empty form submission for the specified form template. The submission is created with no answers yet and no appointment link. The form template must be ACTIVE and belong to the authenticated user\'s organization.  This endpoint is used when creating standalone form submissions, independent of appointments. Pass `clientId` to attach the submission to a client\'s dossier right away — the internal, behandelaar-filled dossier form. The submission can also be linked to a client or appointment later via PATCH.  A submission is always created as a draft. Through the public API it stays one: only a behandelaar marks it reviewed, in Scrivio. 
         * @summary Create a new form submission
         * @param {CreateFormSubmissionRequest} createFormSubmissionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createFormSubmission: async (createFormSubmissionRequest: CreateFormSubmissionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createFormSubmissionRequest' is not null or undefined
            assertParamExists('createFormSubmission', 'createFormSubmissionRequest', createFormSubmissionRequest)
            const localVarPath = `/api/v1/form-submissions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createFormSubmissionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft-deletes a form submission and its associated submission-owned transcription sessions. The submission will be marked as deleted (deletedAt timestamp set) but not physically removed.  Cascade behavior: - Submission-owned TranscriptionSessions (where formSubmissionId = this submission) are also soft-deleted - Appointment-owned TranscriptionSessions are NOT affected 
         * @summary Delete a form submission
         * @param {string} id The ID of the form submission to delete
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteFormSubmission: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'id' is not null or undefined
            assertParamExists('deleteFormSubmission', 'id', id)
            const localVarPath = `/api/v1/form-submissions/{id}`
                .replace(`{${"id"}}`, encodeURIComponent(String(id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the full detail of a form submission including: - Submission metadata (id, createdAt, createdBy) - Form template (id, title, items/questions) - Answers (current values per question) - Linked client information (if any) - Linked appointment information (if any) 
         * @summary Get form submission detail
         * @param {string} submissionId Form Submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormSubmissionDetail: async (submissionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('getFormSubmissionDetail', 'submissionId', submissionId)
            const localVarPath = `/api/v1/form-submissions/{submissionId}`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The same answer as `getFormSubmissionDetail`, at the path it had before it moved under `/api/v1`. Kept only for a browser tab still running a frontend built before the move; it is removed once those have reloaded. Not part of the public API. 
         * @summary Get form submission detail (unversioned path)
         * @param {string} submissionId Form Submission UUID
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getFormSubmissionDetailUnversioned: async (submissionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('getFormSubmissionDetailUnversioned', 'submissionId', submissionId)
            const localVarPath = `/api/form-submissions/{submissionId}`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated, descending-by-time timeline of all audit log entries belonging to the submission and its answers. Combines: - Submission lifecycle events (created, reviewed, deleted) - Answer events (saved/updated) for all answers belonging to the submission  The endpoint owns the \"what counts as belonging to this submission\" logic; the frontend just renders the merged timeline.  Soft-deleted submissions still return their history. 
         * @summary Get audit history for a form submission
         * @param {string} submissionId Form Submission UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormSubmissionHistory: async (submissionId: string, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('getFormSubmissionHistory', 'submissionId', submissionId)
            const localVarPath = `/api/v1/form-submissions/{submissionId}/history`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all form submissions for a specific client, with pagination. Only submissions for forms in the user\'s organization are returned. Submissions are ordered by submission time descending (most recent first). 
         * @summary List form submissions for a client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {boolean} [includeAnswers] When true, each submission carries its answers so the dossier can show the text immediately. Batch-loaded server-side.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientFormSubmissions: async (clientId: string, page?: number, size?: number, includeAnswers?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('listClientFormSubmissions', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/form-submissions`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (includeAnswers !== undefined) {
                localVarQueryParameter['includeAnswers'] = includeAnswers;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all submissions for a specific form. Submissions are ordered by submission time descending (most recent first). Supports filtering by clientId for finding submissions about a specific client. 
         * @summary List form submissions
         * @param {string} formId Form UUID
         * @param {string} [clientId] Filter submissions by the client they pertain to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listFormSubmissions: async (formId: string, clientId?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('listFormSubmissions', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}/submissions`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a paginated list of all form submissions for the authenticated user\'s organization. Submissions are ordered by submission time descending (most recent first). Supports filtering by date range, form template, creator, completion status and whether the linked appointment was cancelled. Every filter is applied in the query, so `totalElements` counts exactly the rows the filters keep (#4670). 
         * @summary List all form submissions for organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {number} [fromDate] Filter submissions from this date (epoch milliseconds, inclusive)
         * @param {number} [toDate] Filter submissions until this date (epoch milliseconds, inclusive)
         * @param {string} [formId] Filter submissions by form template ID
         * @param {string} [createdBy] Filter submissions by the user who created them (userId)
         * @param {string} [clientId] Filter submissions to those that reach this client — filed for the client, submitted by the client through the portal, or an unattributed submission on an appointment the client attends (the same rule as the client\&#39;s own dossier list). Narrows the organization\&#39;s submissions only: a client of another organization matches nothing. 
         * @param {Array<FormCompletionStatus>} [completionStatus] Keep only submissions in one of these completion states. Omitted returns every state. The Formulieren overview omits PENDING by default: an empty draft is not a report, and a page of them hides the ones that are (#4670). 
         * @param {boolean} [excludeCancelledAppointments] Leave out submissions whose linked appointment was cancelled (any of the cancelled statuses). Submissions without an appointment are always kept. Omitted or false keeps them. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationFormSubmissions: async (page?: number, size?: number, fromDate?: number, toDate?: number, formId?: string, createdBy?: string, clientId?: string, completionStatus?: Array<FormCompletionStatus>, excludeCancelledAppointments?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/form-submissions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (fromDate !== undefined) {
                localVarQueryParameter['fromDate'] = fromDate;
            }

            if (toDate !== undefined) {
                localVarQueryParameter['toDate'] = toDate;
            }

            if (formId !== undefined) {
                localVarQueryParameter['formId'] = formId;
            }

            if (createdBy !== undefined) {
                localVarQueryParameter['createdBy'] = createdBy;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (completionStatus) {
                localVarQueryParameter['completionStatus'] = completionStatus;
            }

            if (excludeCancelledAppointments !== undefined) {
                localVarQueryParameter['excludeCancelledAppointments'] = excludeCancelledAppointments;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates an existing form submission. Allows modifying: - formId: Only if no answers have been saved yet - clientId: Link or unlink from a client - appointmentId: Link or unlink from an appointment  When linking to an appointment (setting appointmentId from null to a value): - Any TranscriptionSessions owned by this submission are transferred to the appointment - The sessions\' formSubmissionId is cleared and appointmentId is set  When unlinking from an appointment (setting appointmentId to null): - TranscriptionSessions remain on the appointment (no automatic transfer back)  All referenced entities must belong to the same organization. 
         * @summary Update a form submission
         * @param {string} submissionId Form submission UUID
         * @param {UpdateFormSubmissionRequest} updateFormSubmissionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFormSubmission: async (submissionId: string, updateFormSubmissionRequest: UpdateFormSubmissionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'submissionId' is not null or undefined
            assertParamExists('updateFormSubmission', 'submissionId', submissionId)
            // verify required parameter 'updateFormSubmissionRequest' is not null or undefined
            assertParamExists('updateFormSubmission', 'updateFormSubmissionRequest', updateFormSubmissionRequest)
            const localVarPath = `/api/v1/form-submissions/{submissionId}`
                .replace(`{${"submissionId"}}`, encodeURIComponent(String(submissionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateFormSubmissionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FormSubmissionsApi - functional programming interface
 */
export const FormSubmissionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FormSubmissionsApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates a new empty form submission for the specified form template. The submission is created with no answers yet and no appointment link. The form template must be ACTIVE and belong to the authenticated user\'s organization.  This endpoint is used when creating standalone form submissions, independent of appointments. Pass `clientId` to attach the submission to a client\'s dossier right away — the internal, behandelaar-filled dossier form. The submission can also be linked to a client or appointment later via PATCH.  A submission is always created as a draft. Through the public API it stays one: only a behandelaar marks it reviewed, in Scrivio. 
         * @summary Create a new form submission
         * @param {CreateFormSubmissionRequest} createFormSubmissionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createFormSubmission(createFormSubmissionRequest: CreateFormSubmissionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createFormSubmission(createFormSubmissionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.createFormSubmission']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft-deletes a form submission and its associated submission-owned transcription sessions. The submission will be marked as deleted (deletedAt timestamp set) but not physically removed.  Cascade behavior: - Submission-owned TranscriptionSessions (where formSubmissionId = this submission) are also soft-deleted - Appointment-owned TranscriptionSessions are NOT affected 
         * @summary Delete a form submission
         * @param {string} id The ID of the form submission to delete
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteFormSubmission(id: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteFormSubmission(id, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.deleteFormSubmission']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the full detail of a form submission including: - Submission metadata (id, createdAt, createdBy) - Form template (id, title, items/questions) - Answers (current values per question) - Linked client information (if any) - Linked appointment information (if any) 
         * @summary Get form submission detail
         * @param {string} submissionId Form Submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFormSubmissionDetail(submissionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormSubmissionDetail(submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.getFormSubmissionDetail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The same answer as `getFormSubmissionDetail`, at the path it had before it moved under `/api/v1`. Kept only for a browser tab still running a frontend built before the move; it is removed once those have reloaded. Not part of the public API. 
         * @summary Get form submission detail (unversioned path)
         * @param {string} submissionId Form Submission UUID
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        async getFormSubmissionDetailUnversioned(submissionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormSubmissionDetailUnversioned(submissionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.getFormSubmissionDetailUnversioned']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated, descending-by-time timeline of all audit log entries belonging to the submission and its answers. Combines: - Submission lifecycle events (created, reviewed, deleted) - Answer events (saved/updated) for all answers belonging to the submission  The endpoint owns the \"what counts as belonging to this submission\" logic; the frontend just renders the merged timeline.  Soft-deleted submissions still return their history. 
         * @summary Get audit history for a form submission
         * @param {string} submissionId Form Submission UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFormSubmissionHistory(submissionId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormSubmissionHistory(submissionId, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.getFormSubmissionHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all form submissions for a specific client, with pagination. Only submissions for forms in the user\'s organization are returned. Submissions are ordered by submission time descending (most recent first). 
         * @summary List form submissions for a client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {boolean} [includeAnswers] When true, each submission carries its answers so the dossier can show the text immediately. Batch-loaded server-side.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientFormSubmissions(clientId: string, page?: number, size?: number, includeAnswers?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientFormSubmissionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientFormSubmissions(clientId, page, size, includeAnswers, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.listClientFormSubmissions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all submissions for a specific form. Submissions are ordered by submission time descending (most recent first). Supports filtering by clientId for finding submissions about a specific client. 
         * @summary List form submissions
         * @param {string} formId Form UUID
         * @param {string} [clientId] Filter submissions by the client they pertain to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listFormSubmissions(formId: string, clientId?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listFormSubmissions(formId, clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.listFormSubmissions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a paginated list of all form submissions for the authenticated user\'s organization. Submissions are ordered by submission time descending (most recent first). Supports filtering by date range, form template, creator, completion status and whether the linked appointment was cancelled. Every filter is applied in the query, so `totalElements` counts exactly the rows the filters keep (#4670). 
         * @summary List all form submissions for organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {number} [fromDate] Filter submissions from this date (epoch milliseconds, inclusive)
         * @param {number} [toDate] Filter submissions until this date (epoch milliseconds, inclusive)
         * @param {string} [formId] Filter submissions by form template ID
         * @param {string} [createdBy] Filter submissions by the user who created them (userId)
         * @param {string} [clientId] Filter submissions to those that reach this client — filed for the client, submitted by the client through the portal, or an unattributed submission on an appointment the client attends (the same rule as the client\&#39;s own dossier list). Narrows the organization\&#39;s submissions only: a client of another organization matches nothing. 
         * @param {Array<FormCompletionStatus>} [completionStatus] Keep only submissions in one of these completion states. Omitted returns every state. The Formulieren overview omits PENDING by default: an empty draft is not a report, and a page of them hides the ones that are (#4670). 
         * @param {boolean} [excludeCancelledAppointments] Leave out submissions whose linked appointment was cancelled (any of the cancelled statuses). Submissions without an appointment are always kept. Omitted or false keeps them. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listOrganizationFormSubmissions(page?: number, size?: number, fromDate?: number, toDate?: number, formId?: string, createdBy?: string, clientId?: string, completionStatus?: Array<FormCompletionStatus>, excludeCancelledAppointments?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationFormSubmissionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listOrganizationFormSubmissions(page, size, fromDate, toDate, formId, createdBy, clientId, completionStatus, excludeCancelledAppointments, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.listOrganizationFormSubmissions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates an existing form submission. Allows modifying: - formId: Only if no answers have been saved yet - clientId: Link or unlink from a client - appointmentId: Link or unlink from an appointment  When linking to an appointment (setting appointmentId from null to a value): - Any TranscriptionSessions owned by this submission are transferred to the appointment - The sessions\' formSubmissionId is cleared and appointmentId is set  When unlinking from an appointment (setting appointmentId to null): - TranscriptionSessions remain on the appointment (no automatic transfer back)  All referenced entities must belong to the same organization. 
         * @summary Update a form submission
         * @param {string} submissionId Form submission UUID
         * @param {UpdateFormSubmissionRequest} updateFormSubmissionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateFormSubmission(submissionId: string, updateFormSubmissionRequest: UpdateFormSubmissionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormSubmissionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateFormSubmission(submissionId, updateFormSubmissionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormSubmissionsApi.updateFormSubmission']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FormSubmissionsApi - factory interface
 */
export const FormSubmissionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FormSubmissionsApiFp(configuration)
    return {
        /**
         * Creates a new empty form submission for the specified form template. The submission is created with no answers yet and no appointment link. The form template must be ACTIVE and belong to the authenticated user\'s organization.  This endpoint is used when creating standalone form submissions, independent of appointments. Pass `clientId` to attach the submission to a client\'s dossier right away — the internal, behandelaar-filled dossier form. The submission can also be linked to a client or appointment later via PATCH.  A submission is always created as a draft. Through the public API it stays one: only a behandelaar marks it reviewed, in Scrivio. 
         * @summary Create a new form submission
         * @param {CreateFormSubmissionRequest} createFormSubmissionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createFormSubmission(createFormSubmissionRequest: CreateFormSubmissionRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionResponse> {
            return localVarFp.createFormSubmission(createFormSubmissionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft-deletes a form submission and its associated submission-owned transcription sessions. The submission will be marked as deleted (deletedAt timestamp set) but not physically removed.  Cascade behavior: - Submission-owned TranscriptionSessions (where formSubmissionId = this submission) are also soft-deleted - Appointment-owned TranscriptionSessions are NOT affected 
         * @summary Delete a form submission
         * @param {string} id The ID of the form submission to delete
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteFormSubmission(id: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteFormSubmission(id, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the full detail of a form submission including: - Submission metadata (id, createdAt, createdBy) - Form template (id, title, items/questions) - Answers (current values per question) - Linked client information (if any) - Linked appointment information (if any) 
         * @summary Get form submission detail
         * @param {string} submissionId Form Submission UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormSubmissionDetail(submissionId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionDetailResponse> {
            return localVarFp.getFormSubmissionDetail(submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * The same answer as `getFormSubmissionDetail`, at the path it had before it moved under `/api/v1`. Kept only for a browser tab still running a frontend built before the move; it is removed once those have reloaded. Not part of the public API. 
         * @summary Get form submission detail (unversioned path)
         * @param {string} submissionId Form Submission UUID
         * @param {*} [options] Override http request option.
         * @deprecated
         * @throws {RequiredError}
         */
        getFormSubmissionDetailUnversioned(submissionId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionDetailResponse> {
            return localVarFp.getFormSubmissionDetailUnversioned(submissionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated, descending-by-time timeline of all audit log entries belonging to the submission and its answers. Combines: - Submission lifecycle events (created, reviewed, deleted) - Answer events (saved/updated) for all answers belonging to the submission  The endpoint owns the \"what counts as belonging to this submission\" logic; the frontend just renders the merged timeline.  Soft-deleted submissions still return their history. 
         * @summary Get audit history for a form submission
         * @param {string} submissionId Form Submission UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Page size
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormSubmissionHistory(submissionId: string, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionHistoryResponse> {
            return localVarFp.getFormSubmissionHistory(submissionId, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all form submissions for a specific client, with pagination. Only submissions for forms in the user\'s organization are returned. Submissions are ordered by submission time descending (most recent first). 
         * @summary List form submissions for a client
         * @param {string} clientId Client UUID
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {boolean} [includeAnswers] When true, each submission carries its answers so the dossier can show the text immediately. Batch-loaded server-side.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientFormSubmissions(clientId: string, page?: number, size?: number, includeAnswers?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<ClientFormSubmissionListResponse> {
            return localVarFp.listClientFormSubmissions(clientId, page, size, includeAnswers, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all submissions for a specific form. Submissions are ordered by submission time descending (most recent first). Supports filtering by clientId for finding submissions about a specific client. 
         * @summary List form submissions
         * @param {string} formId Form UUID
         * @param {string} [clientId] Filter submissions by the client they pertain to
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listFormSubmissions(formId: string, clientId?: string, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionListResponse> {
            return localVarFp.listFormSubmissions(formId, clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a paginated list of all form submissions for the authenticated user\'s organization. Submissions are ordered by submission time descending (most recent first). Supports filtering by date range, form template, creator, completion status and whether the linked appointment was cancelled. Every filter is applied in the query, so `totalElements` counts exactly the rows the filters keep (#4670). 
         * @summary List all form submissions for organization
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {number} [fromDate] Filter submissions from this date (epoch milliseconds, inclusive)
         * @param {number} [toDate] Filter submissions until this date (epoch milliseconds, inclusive)
         * @param {string} [formId] Filter submissions by form template ID
         * @param {string} [createdBy] Filter submissions by the user who created them (userId)
         * @param {string} [clientId] Filter submissions to those that reach this client — filed for the client, submitted by the client through the portal, or an unattributed submission on an appointment the client attends (the same rule as the client\&#39;s own dossier list). Narrows the organization\&#39;s submissions only: a client of another organization matches nothing. 
         * @param {Array<FormCompletionStatus>} [completionStatus] Keep only submissions in one of these completion states. Omitted returns every state. The Formulieren overview omits PENDING by default: an empty draft is not a report, and a page of them hides the ones that are (#4670). 
         * @param {boolean} [excludeCancelledAppointments] Leave out submissions whose linked appointment was cancelled (any of the cancelled statuses). Submissions without an appointment are always kept. Omitted or false keeps them. 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listOrganizationFormSubmissions(page?: number, size?: number, fromDate?: number, toDate?: number, formId?: string, createdBy?: string, clientId?: string, completionStatus?: Array<FormCompletionStatus>, excludeCancelledAppointments?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationFormSubmissionListResponse> {
            return localVarFp.listOrganizationFormSubmissions(page, size, fromDate, toDate, formId, createdBy, clientId, completionStatus, excludeCancelledAppointments, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates an existing form submission. Allows modifying: - formId: Only if no answers have been saved yet - clientId: Link or unlink from a client - appointmentId: Link or unlink from an appointment  When linking to an appointment (setting appointmentId from null to a value): - Any TranscriptionSessions owned by this submission are transferred to the appointment - The sessions\' formSubmissionId is cleared and appointmentId is set  When unlinking from an appointment (setting appointmentId to null): - TranscriptionSessions remain on the appointment (no automatic transfer back)  All referenced entities must belong to the same organization. 
         * @summary Update a form submission
         * @param {string} submissionId Form submission UUID
         * @param {UpdateFormSubmissionRequest} updateFormSubmissionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateFormSubmission(submissionId: string, updateFormSubmissionRequest: UpdateFormSubmissionRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormSubmissionResponse> {
            return localVarFp.updateFormSubmission(submissionId, updateFormSubmissionRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FormSubmissionsApi - object-oriented interface
 */
export class FormSubmissionsApi extends BaseAPI {
    /**
     * Creates a new empty form submission for the specified form template. The submission is created with no answers yet and no appointment link. The form template must be ACTIVE and belong to the authenticated user\'s organization.  This endpoint is used when creating standalone form submissions, independent of appointments. Pass `clientId` to attach the submission to a client\'s dossier right away — the internal, behandelaar-filled dossier form. The submission can also be linked to a client or appointment later via PATCH.  A submission is always created as a draft. Through the public API it stays one: only a behandelaar marks it reviewed, in Scrivio. 
     * @summary Create a new form submission
     * @param {CreateFormSubmissionRequest} createFormSubmissionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createFormSubmission(createFormSubmissionRequest: CreateFormSubmissionRequest, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).createFormSubmission(createFormSubmissionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft-deletes a form submission and its associated submission-owned transcription sessions. The submission will be marked as deleted (deletedAt timestamp set) but not physically removed.  Cascade behavior: - Submission-owned TranscriptionSessions (where formSubmissionId = this submission) are also soft-deleted - Appointment-owned TranscriptionSessions are NOT affected 
     * @summary Delete a form submission
     * @param {string} id The ID of the form submission to delete
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteFormSubmission(id: string, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).deleteFormSubmission(id, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the full detail of a form submission including: - Submission metadata (id, createdAt, createdBy) - Form template (id, title, items/questions) - Answers (current values per question) - Linked client information (if any) - Linked appointment information (if any) 
     * @summary Get form submission detail
     * @param {string} submissionId Form Submission UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFormSubmissionDetail(submissionId: string, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).getFormSubmissionDetail(submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The same answer as `getFormSubmissionDetail`, at the path it had before it moved under `/api/v1`. Kept only for a browser tab still running a frontend built before the move; it is removed once those have reloaded. Not part of the public API. 
     * @summary Get form submission detail (unversioned path)
     * @param {string} submissionId Form Submission UUID
     * @param {*} [options] Override http request option.
     * @deprecated
     * @throws {RequiredError}
     */
    public getFormSubmissionDetailUnversioned(submissionId: string, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).getFormSubmissionDetailUnversioned(submissionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated, descending-by-time timeline of all audit log entries belonging to the submission and its answers. Combines: - Submission lifecycle events (created, reviewed, deleted) - Answer events (saved/updated) for all answers belonging to the submission  The endpoint owns the \"what counts as belonging to this submission\" logic; the frontend just renders the merged timeline.  Soft-deleted submissions still return their history. 
     * @summary Get audit history for a form submission
     * @param {string} submissionId Form Submission UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Page size
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFormSubmissionHistory(submissionId: string, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).getFormSubmissionHistory(submissionId, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all form submissions for a specific client, with pagination. Only submissions for forms in the user\'s organization are returned. Submissions are ordered by submission time descending (most recent first). 
     * @summary List form submissions for a client
     * @param {string} clientId Client UUID
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {boolean} [includeAnswers] When true, each submission carries its answers so the dossier can show the text immediately. Batch-loaded server-side.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientFormSubmissions(clientId: string, page?: number, size?: number, includeAnswers?: boolean, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).listClientFormSubmissions(clientId, page, size, includeAnswers, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all submissions for a specific form. Submissions are ordered by submission time descending (most recent first). Supports filtering by clientId for finding submissions about a specific client. 
     * @summary List form submissions
     * @param {string} formId Form UUID
     * @param {string} [clientId] Filter submissions by the client they pertain to
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listFormSubmissions(formId: string, clientId?: string, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).listFormSubmissions(formId, clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a paginated list of all form submissions for the authenticated user\'s organization. Submissions are ordered by submission time descending (most recent first). Supports filtering by date range, form template, creator, completion status and whether the linked appointment was cancelled. Every filter is applied in the query, so `totalElements` counts exactly the rows the filters keep (#4670). 
     * @summary List all form submissions for organization
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {number} [fromDate] Filter submissions from this date (epoch milliseconds, inclusive)
     * @param {number} [toDate] Filter submissions until this date (epoch milliseconds, inclusive)
     * @param {string} [formId] Filter submissions by form template ID
     * @param {string} [createdBy] Filter submissions by the user who created them (userId)
     * @param {string} [clientId] Filter submissions to those that reach this client — filed for the client, submitted by the client through the portal, or an unattributed submission on an appointment the client attends (the same rule as the client\&#39;s own dossier list). Narrows the organization\&#39;s submissions only: a client of another organization matches nothing. 
     * @param {Array<FormCompletionStatus>} [completionStatus] Keep only submissions in one of these completion states. Omitted returns every state. The Formulieren overview omits PENDING by default: an empty draft is not a report, and a page of them hides the ones that are (#4670). 
     * @param {boolean} [excludeCancelledAppointments] Leave out submissions whose linked appointment was cancelled (any of the cancelled statuses). Submissions without an appointment are always kept. Omitted or false keeps them. 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listOrganizationFormSubmissions(page?: number, size?: number, fromDate?: number, toDate?: number, formId?: string, createdBy?: string, clientId?: string, completionStatus?: Array<FormCompletionStatus>, excludeCancelledAppointments?: boolean, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).listOrganizationFormSubmissions(page, size, fromDate, toDate, formId, createdBy, clientId, completionStatus, excludeCancelledAppointments, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates an existing form submission. Allows modifying: - formId: Only if no answers have been saved yet - clientId: Link or unlink from a client - appointmentId: Link or unlink from an appointment  When linking to an appointment (setting appointmentId from null to a value): - Any TranscriptionSessions owned by this submission are transferred to the appointment - The sessions\' formSubmissionId is cleared and appointmentId is set  When unlinking from an appointment (setting appointmentId to null): - TranscriptionSessions remain on the appointment (no automatic transfer back)  All referenced entities must belong to the same organization. 
     * @summary Update a form submission
     * @param {string} submissionId Form submission UUID
     * @param {UpdateFormSubmissionRequest} updateFormSubmissionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateFormSubmission(submissionId: string, updateFormSubmissionRequest: UpdateFormSubmissionRequest, options?: RawAxiosRequestConfig) {
        return FormSubmissionsApiFp(this.configuration).updateFormSubmission(submissionId, updateFormSubmissionRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * FormsApi - axios parameter creator
 */
export const FormsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Transitions a form from DRAFT to ACTIVE status. Only forms in DRAFT status can be activated. 
         * @summary Activate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateForm: async (formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('activateForm', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}/activate`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new form in DRAFT status for the authenticated user\'s organization.
         * @summary Create a new form
         * @param {CreateFormRequest} createFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createForm: async (createFormRequest: CreateFormRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createFormRequest' is not null or undefined
            assertParamExists('createForm', 'createFormRequest', createFormRequest)
            const localVarPath = `/api/v1/forms`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createFormRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Soft-deletes a form. Always allowed: the form is detached from every appointment type it is linked to and its empty draft submissions are soft-deleted with it, so it stops appearing anywhere from now on. Submissions that contain answers are kept and remain readable in the dossier. 
         * @summary Delete a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteForm: async (formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('deleteForm', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Transitions a form from ACTIVE to DEPRECATED status. Only forms in ACTIVE status can be deprecated. 
         * @summary Deprecate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deprecateForm: async (formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('deprecateForm', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}/deprecate`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a form with all its items.
         * @summary Get a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getForm: async (formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('getForm', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns whether the authenticated user\'s organization uses formulieren, i.e. has at least one non-deleted form configured. Used by the frontend to conditionally show forms-related navigation. 
         * @summary Get forms usage for the organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormsUsage: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/forms/usage`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all non-deleted forms for the authenticated user\'s organization. 
         * @summary List all forms
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listForms: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/forms`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Transitions a form from DEPRECATED back to ACTIVE status. Only forms in DEPRECATED status can be reactivated. 
         * @summary Reactivate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reactivateForm: async (formId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('reactivateForm', 'formId', formId)
            const localVarPath = `/api/v1/forms/{formId}/reactivate`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates a form\'s title, description, and target flags. Only forms in DRAFT status can be fully updated. 
         * @summary Update a form
         * @param {string} formId Form UUID
         * @param {UpdateFormRequest} updateFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateForm: async (formId: string, updateFormRequest: UpdateFormRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'formId' is not null or undefined
            assertParamExists('updateForm', 'formId', formId)
            // verify required parameter 'updateFormRequest' is not null or undefined
            assertParamExists('updateForm', 'updateFormRequest', updateFormRequest)
            const localVarPath = `/api/v1/forms/{formId}`
                .replace(`{${"formId"}}`, encodeURIComponent(String(formId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateFormRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * FormsApi - functional programming interface
 */
export const FormsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = FormsApiAxiosParamCreator(configuration)
    return {
        /**
         * Transitions a form from DRAFT to ACTIVE status. Only forms in DRAFT status can be activated. 
         * @summary Activate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async activateForm(formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.activateForm(formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.activateForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new form in DRAFT status for the authenticated user\'s organization.
         * @summary Create a new form
         * @param {CreateFormRequest} createFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createForm(createFormRequest: CreateFormRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createForm(createFormRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.createForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Soft-deletes a form. Always allowed: the form is detached from every appointment type it is linked to and its empty draft submissions are soft-deleted with it, so it stops appearing anywhere from now on. Submissions that contain answers are kept and remain readable in the dossier. 
         * @summary Delete a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteForm(formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteForm(formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.deleteForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Transitions a form from ACTIVE to DEPRECATED status. Only forms in ACTIVE status can be deprecated. 
         * @summary Deprecate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deprecateForm(formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deprecateForm(formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.deprecateForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a form with all its items.
         * @summary Get a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getForm(formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getForm(formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.getForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns whether the authenticated user\'s organization uses formulieren, i.e. has at least one non-deleted form configured. Used by the frontend to conditionally show forms-related navigation. 
         * @summary Get forms usage for the organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getFormsUsage(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormsUsageResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getFormsUsage(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.getFormsUsage']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all non-deleted forms for the authenticated user\'s organization. 
         * @summary List all forms
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listForms(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listForms(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.listForms']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Transitions a form from DEPRECATED back to ACTIVE status. Only forms in DEPRECATED status can be reactivated. 
         * @summary Reactivate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async reactivateForm(formId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.reactivateForm(formId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.reactivateForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates a form\'s title, description, and target flags. Only forms in DRAFT status can be fully updated. 
         * @summary Update a form
         * @param {string} formId Form UUID
         * @param {UpdateFormRequest} updateFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateForm(formId: string, updateFormRequest: UpdateFormRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<FormResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateForm(formId, updateFormRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['FormsApi.updateForm']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * FormsApi - factory interface
 */
export const FormsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = FormsApiFp(configuration)
    return {
        /**
         * Transitions a form from DRAFT to ACTIVE status. Only forms in DRAFT status can be activated. 
         * @summary Activate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        activateForm(formId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.activateForm(formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new form in DRAFT status for the authenticated user\'s organization.
         * @summary Create a new form
         * @param {CreateFormRequest} createFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createForm(createFormRequest: CreateFormRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.createForm(createFormRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Soft-deletes a form. Always allowed: the form is detached from every appointment type it is linked to and its empty draft submissions are soft-deleted with it, so it stops appearing anywhere from now on. Submissions that contain answers are kept and remain readable in the dossier. 
         * @summary Delete a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteForm(formId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteForm(formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Transitions a form from ACTIVE to DEPRECATED status. Only forms in ACTIVE status can be deprecated. 
         * @summary Deprecate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deprecateForm(formId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.deprecateForm(formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a form with all its items.
         * @summary Get a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getForm(formId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.getForm(formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns whether the authenticated user\'s organization uses formulieren, i.e. has at least one non-deleted form configured. Used by the frontend to conditionally show forms-related navigation. 
         * @summary Get forms usage for the organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getFormsUsage(options?: RawAxiosRequestConfig): AxiosPromise<FormsUsageResponse> {
            return localVarFp.getFormsUsage(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all non-deleted forms for the authenticated user\'s organization. 
         * @summary List all forms
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listForms(options?: RawAxiosRequestConfig): AxiosPromise<FormListResponse> {
            return localVarFp.listForms(options).then((request) => request(axios, basePath));
        },
        /**
         * Transitions a form from DEPRECATED back to ACTIVE status. Only forms in DEPRECATED status can be reactivated. 
         * @summary Reactivate a form
         * @param {string} formId Form UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        reactivateForm(formId: string, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.reactivateForm(formId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates a form\'s title, description, and target flags. Only forms in DRAFT status can be fully updated. 
         * @summary Update a form
         * @param {string} formId Form UUID
         * @param {UpdateFormRequest} updateFormRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateForm(formId: string, updateFormRequest: UpdateFormRequest, options?: RawAxiosRequestConfig): AxiosPromise<FormResponse> {
            return localVarFp.updateForm(formId, updateFormRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * FormsApi - object-oriented interface
 */
export class FormsApi extends BaseAPI {
    /**
     * Transitions a form from DRAFT to ACTIVE status. Only forms in DRAFT status can be activated. 
     * @summary Activate a form
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public activateForm(formId: string, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).activateForm(formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new form in DRAFT status for the authenticated user\'s organization.
     * @summary Create a new form
     * @param {CreateFormRequest} createFormRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createForm(createFormRequest: CreateFormRequest, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).createForm(createFormRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Soft-deletes a form. Always allowed: the form is detached from every appointment type it is linked to and its empty draft submissions are soft-deleted with it, so it stops appearing anywhere from now on. Submissions that contain answers are kept and remain readable in the dossier. 
     * @summary Delete a form
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteForm(formId: string, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).deleteForm(formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Transitions a form from ACTIVE to DEPRECATED status. Only forms in ACTIVE status can be deprecated. 
     * @summary Deprecate a form
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deprecateForm(formId: string, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).deprecateForm(formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a form with all its items.
     * @summary Get a form
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getForm(formId: string, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).getForm(formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns whether the authenticated user\'s organization uses formulieren, i.e. has at least one non-deleted form configured. Used by the frontend to conditionally show forms-related navigation. 
     * @summary Get forms usage for the organization
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getFormsUsage(options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).getFormsUsage(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all non-deleted forms for the authenticated user\'s organization. 
     * @summary List all forms
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listForms(options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).listForms(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Transitions a form from DEPRECATED back to ACTIVE status. Only forms in DEPRECATED status can be reactivated. 
     * @summary Reactivate a form
     * @param {string} formId Form UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public reactivateForm(formId: string, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).reactivateForm(formId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates a form\'s title, description, and target flags. Only forms in DRAFT status can be fully updated. 
     * @summary Update a form
     * @param {string} formId Form UUID
     * @param {UpdateFormRequest} updateFormRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateForm(formId: string, updateFormRequest: UpdateFormRequest, options?: RawAxiosRequestConfig) {
        return FormsApiFp(this.configuration).updateForm(formId, updateFormRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * QuestionsApi - axios parameter creator
 */
export const QuestionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Adds an inline question to the reusable question library. Once added, the question will appear in the library picker for adding to other forms. Questions created via the standard create endpoint are added to the library by default. 
         * @summary Add a question to the reusable library
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addQuestionToLibrary: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('addQuestionToLibrary', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}/add-to-library`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Archives a question. Archived questions are hidden from the default list but remain accessible for historical form responses and letter template rendering. This is the preferred way to \"remove\" questions that have existing answers. 
         * @summary Archive a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveQuestion: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('archiveQuestion', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}/archive`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns whether a question has any answers associated with it. Questions with answers cannot be deleted but can be archived. 
         * @summary Check if question has answers
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkQuestionHasAnswers: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('checkQuestionHasAnswers', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}/has-answers`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new question with version 1 in the authenticated user\'s organization.
         * @summary Create a new question
         * @param {CreateQuestionRequest} createQuestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createQuestion: async (createQuestionRequest: CreateQuestionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createQuestionRequest' is not null or undefined
            assertParamExists('createQuestion', 'createQuestionRequest', createQuestionRequest)
            const localVarPath = `/api/v1/questions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createQuestionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a question and all its versions. Returns 409 Conflict if the question has associated answers. 
         * @summary Delete a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteQuestion: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('deleteQuestion', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a question with its latest version.
         * @summary Get a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getQuestion: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('getQuestion', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all versions of a question, ordered by version number descending (most recent first). 
         * @summary Get question version history
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getQuestionVersions: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('getQuestionVersions', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}/versions`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all questions for the authenticated user\'s organization. Each question includes its latest version. By default, archived questions are not included. 
         * @summary List all questions
         * @param {boolean} [includeArchived] If true, includes archived questions in the result
         * @param {boolean} [libraryOnly] If true, only returns questions that are in the reusable library
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listQuestions: async (includeArchived?: boolean, libraryOnly?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/questions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (includeArchived !== undefined) {
                localVarQueryParameter['includeArchived'] = includeArchived;
            }

            if (libraryOnly !== undefined) {
                localVarQueryParameter['libraryOnly'] = libraryOnly;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Unarchives (restores) a previously archived question. The question will appear again in the default questions list. 
         * @summary Unarchive a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveQuestion: async (questionId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('unarchiveQuestion', 'questionId', questionId)
            const localVarPath = `/api/v1/questions/{questionId}/unarchive`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates a question by creating a new version with the provided data. The question type cannot be changed. 
         * @summary Update a question
         * @param {string} questionId Question UUID
         * @param {UpdateQuestionRequest} updateQuestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateQuestion: async (questionId: string, updateQuestionRequest: UpdateQuestionRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'questionId' is not null or undefined
            assertParamExists('updateQuestion', 'questionId', questionId)
            // verify required parameter 'updateQuestionRequest' is not null or undefined
            assertParamExists('updateQuestion', 'updateQuestionRequest', updateQuestionRequest)
            const localVarPath = `/api/v1/questions/{questionId}`
                .replace(`{${"questionId"}}`, encodeURIComponent(String(questionId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateQuestionRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * QuestionsApi - functional programming interface
 */
export const QuestionsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = QuestionsApiAxiosParamCreator(configuration)
    return {
        /**
         * Adds an inline question to the reusable question library. Once added, the question will appear in the library picker for adding to other forms. Questions created via the standard create endpoint are added to the library by default. 
         * @summary Add a question to the reusable library
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async addQuestionToLibrary(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.addQuestionToLibrary(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.addQuestionToLibrary']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Archives a question. Archived questions are hidden from the default list but remain accessible for historical form responses and letter template rendering. This is the preferred way to \"remove\" questions that have existing answers. 
         * @summary Archive a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async archiveQuestion(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.archiveQuestion(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.archiveQuestion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns whether a question has any answers associated with it. Questions with answers cannot be deleted but can be archived. 
         * @summary Check if question has answers
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkQuestionHasAnswers(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionHasAnswersResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkQuestionHasAnswers(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.checkQuestionHasAnswers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new question with version 1 in the authenticated user\'s organization.
         * @summary Create a new question
         * @param {CreateQuestionRequest} createQuestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createQuestion(createQuestionRequest: CreateQuestionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createQuestion(createQuestionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.createQuestion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a question and all its versions. Returns 409 Conflict if the question has associated answers. 
         * @summary Delete a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteQuestion(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteQuestion(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.deleteQuestion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a question with its latest version.
         * @summary Get a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getQuestion(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getQuestion(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.getQuestion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all versions of a question, ordered by version number descending (most recent first). 
         * @summary Get question version history
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getQuestionVersions(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionVersionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getQuestionVersions(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.getQuestionVersions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all questions for the authenticated user\'s organization. Each question includes its latest version. By default, archived questions are not included. 
         * @summary List all questions
         * @param {boolean} [includeArchived] If true, includes archived questions in the result
         * @param {boolean} [libraryOnly] If true, only returns questions that are in the reusable library
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listQuestions(includeArchived?: boolean, libraryOnly?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listQuestions(includeArchived, libraryOnly, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.listQuestions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Unarchives (restores) a previously archived question. The question will appear again in the default questions list. 
         * @summary Unarchive a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unarchiveQuestion(questionId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unarchiveQuestion(questionId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.unarchiveQuestion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates a question by creating a new version with the provided data. The question type cannot be changed. 
         * @summary Update a question
         * @param {string} questionId Question UUID
         * @param {UpdateQuestionRequest} updateQuestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateQuestion(questionId: string, updateQuestionRequest: UpdateQuestionRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<QuestionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateQuestion(questionId, updateQuestionRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['QuestionsApi.updateQuestion']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * QuestionsApi - factory interface
 */
export const QuestionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = QuestionsApiFp(configuration)
    return {
        /**
         * Adds an inline question to the reusable question library. Once added, the question will appear in the library picker for adding to other forms. Questions created via the standard create endpoint are added to the library by default. 
         * @summary Add a question to the reusable library
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        addQuestionToLibrary(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<QuestionResponse> {
            return localVarFp.addQuestionToLibrary(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Archives a question. Archived questions are hidden from the default list but remain accessible for historical form responses and letter template rendering. This is the preferred way to \"remove\" questions that have existing answers. 
         * @summary Archive a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        archiveQuestion(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<QuestionResponse> {
            return localVarFp.archiveQuestion(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns whether a question has any answers associated with it. Questions with answers cannot be deleted but can be archived. 
         * @summary Check if question has answers
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkQuestionHasAnswers(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<QuestionHasAnswersResponse> {
            return localVarFp.checkQuestionHasAnswers(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new question with version 1 in the authenticated user\'s organization.
         * @summary Create a new question
         * @param {CreateQuestionRequest} createQuestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createQuestion(createQuestionRequest: CreateQuestionRequest, options?: RawAxiosRequestConfig): AxiosPromise<QuestionResponse> {
            return localVarFp.createQuestion(createQuestionRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a question and all its versions. Returns 409 Conflict if the question has associated answers. 
         * @summary Delete a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteQuestion(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteQuestion(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a question with its latest version.
         * @summary Get a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getQuestion(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<QuestionResponse> {
            return localVarFp.getQuestion(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all versions of a question, ordered by version number descending (most recent first). 
         * @summary Get question version history
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getQuestionVersions(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<QuestionVersionListResponse> {
            return localVarFp.getQuestionVersions(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all questions for the authenticated user\'s organization. Each question includes its latest version. By default, archived questions are not included. 
         * @summary List all questions
         * @param {boolean} [includeArchived] If true, includes archived questions in the result
         * @param {boolean} [libraryOnly] If true, only returns questions that are in the reusable library
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listQuestions(includeArchived?: boolean, libraryOnly?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<QuestionListResponse> {
            return localVarFp.listQuestions(includeArchived, libraryOnly, options).then((request) => request(axios, basePath));
        },
        /**
         * Unarchives (restores) a previously archived question. The question will appear again in the default questions list. 
         * @summary Unarchive a question
         * @param {string} questionId Question UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unarchiveQuestion(questionId: string, options?: RawAxiosRequestConfig): AxiosPromise<QuestionResponse> {
            return localVarFp.unarchiveQuestion(questionId, options).then((request) => request(axios, basePath));
        },
        /**
         * Updates a question by creating a new version with the provided data. The question type cannot be changed. 
         * @summary Update a question
         * @param {string} questionId Question UUID
         * @param {UpdateQuestionRequest} updateQuestionRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateQuestion(questionId: string, updateQuestionRequest: UpdateQuestionRequest, options?: RawAxiosRequestConfig): AxiosPromise<QuestionResponse> {
            return localVarFp.updateQuestion(questionId, updateQuestionRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * QuestionsApi - object-oriented interface
 */
export class QuestionsApi extends BaseAPI {
    /**
     * Adds an inline question to the reusable question library. Once added, the question will appear in the library picker for adding to other forms. Questions created via the standard create endpoint are added to the library by default. 
     * @summary Add a question to the reusable library
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public addQuestionToLibrary(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).addQuestionToLibrary(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Archives a question. Archived questions are hidden from the default list but remain accessible for historical form responses and letter template rendering. This is the preferred way to \"remove\" questions that have existing answers. 
     * @summary Archive a question
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public archiveQuestion(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).archiveQuestion(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns whether a question has any answers associated with it. Questions with answers cannot be deleted but can be archived. 
     * @summary Check if question has answers
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public checkQuestionHasAnswers(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).checkQuestionHasAnswers(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new question with version 1 in the authenticated user\'s organization.
     * @summary Create a new question
     * @param {CreateQuestionRequest} createQuestionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createQuestion(createQuestionRequest: CreateQuestionRequest, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).createQuestion(createQuestionRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a question and all its versions. Returns 409 Conflict if the question has associated answers. 
     * @summary Delete a question
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteQuestion(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).deleteQuestion(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a question with its latest version.
     * @summary Get a question
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getQuestion(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).getQuestion(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all versions of a question, ordered by version number descending (most recent first). 
     * @summary Get question version history
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getQuestionVersions(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).getQuestionVersions(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all questions for the authenticated user\'s organization. Each question includes its latest version. By default, archived questions are not included. 
     * @summary List all questions
     * @param {boolean} [includeArchived] If true, includes archived questions in the result
     * @param {boolean} [libraryOnly] If true, only returns questions that are in the reusable library
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listQuestions(includeArchived?: boolean, libraryOnly?: boolean, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).listQuestions(includeArchived, libraryOnly, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Unarchives (restores) a previously archived question. The question will appear again in the default questions list. 
     * @summary Unarchive a question
     * @param {string} questionId Question UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unarchiveQuestion(questionId: string, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).unarchiveQuestion(questionId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates a question by creating a new version with the provided data. The question type cannot be changed. 
     * @summary Update a question
     * @param {string} questionId Question UUID
     * @param {UpdateQuestionRequest} updateQuestionRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateQuestion(questionId: string, updateQuestionRequest: UpdateQuestionRequest, options?: RawAxiosRequestConfig) {
        return QuestionsApiFp(this.configuration).updateQuestion(questionId, updateQuestionRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



